'use client'

export const runtime = 'edge'

import {
  AlertTriangle,
  Bot,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Filter,
  Flag,
  Globe,
  Loader2,
  LogOut,
  MessageSquare,
  Monitor,
  PanelLeftOpen,
  Phone,
  RefreshCw,
  Shield,
  Smartphone,
  User,
  Users,
  Wifi,
  WifiOff,
  X,
} from 'lucide-react'
import type {
  CSSProperties,
  JSX,
  PointerEvent as ReactPointerEvent,
} from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import { HandoverNotifications } from '@/components/agent/HandoverNotifications'
import { NotificationCenter } from '@/components/notifications/NotificationCenter'
import { TeamFilter } from '@/components/ui/team-filter'
import { usePathname, useRouter } from '@/i18n/navigation'
import { useNotifications, useNotificationActions } from '@/lib/notifications'
import { useToastNotificationStore } from '@/lib/toast-notifications'
import { buildAgentDashboardChatUrl, formatRelativeTime } from '@/lib/utils'
import { derivePrimaryTeamId, normalizeTeamIds } from '@/lib/utils/teams'
import { buildPermissionSet, hasPermission } from '@/lib/permissions'
import { useWebSocket } from '@/lib/websocket'
import { useAgentAuth } from '@/stores/auth'
import { useTeamsStore } from '@/stores/teams'
import { useAgentChatStore } from '@/stores/agentChat'
import { useNotificationStore } from '@/lib/notifications'
import { api } from '@/lib/api'
import { extractUserRoles, getPrimaryRole } from '@/lib/roles'
import { UserRole } from '@/types/roles'
import { OutboundMessaging } from '@/components/agent/OutboundMessaging'
import { AssignedSessionsSidebar } from '@/components/agent/AssignedSessionsSidebar'
import { AgentSelect } from '@/components/agent/AgentSelect'
import {
  SupervisorManageDialog,
  type SupervisorAvailableAgent,
  type SupervisorManageDialogSubmit,
} from '@/components/agent/SupervisorManageDialog'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { AgentChatPanel } from '@/components/agent/AgentChatPanel'
import { AgentArticlesPanel } from '@/components/agent/AgentArticlesPanel'
import { AgentConfigurationPanel } from '@/components/agent/AgentConfigurationPanel'
import { AgentTicketsPanel } from '@/components/agent/AgentTicketsPanel'
import { useSidebarState } from '@/stores/ui'
import type { SessionTakeoverOptions } from '@/lib/api/services/sessions'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useTeamNameById } from '@/hooks/useTeamNameById'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import { HandoverRequest } from '@/types'
import type {
  ConversationCountReport,
  ConversationPerformanceAgentMetrics,
  ConversationPerformanceReport,
} from '@/types/reports'
import { useSearchParams } from 'next/navigation'
import type { FlowAttachment } from '@/types/flow'
import type {
  Visitor,
  DashboardStats,
  ActiveSession,
  AllActiveSession,
  ConversationReportBucket,
  ReportSectionId,
  ReportFilterKey,
  ReportFilterState,
  ReportSection,
  ConversationsCountReportSectionProps,
  PerformanceReportSectionProps,
} from './dashboard.interface'
import ErrorBoundary from '@/components/chat/ErrorBoundary'
import { useAllSessionsFiltering } from '@/hooks/useAllSessionsFiltering'
import { useTakeoverTeamModal } from '@/hooks/useTakeoverTeamModal'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
)

// DEFAULT_ALL_SESSION_FILTERS moved into useAllSessionsFiltering hook

const formatConversationBucketLabel = (
  isoTimestamp: string,
  bucket: ConversationReportBucket
) => {
  const timestamp = new Date(isoTimestamp)
  if (Number.isNaN(timestamp.getTime())) {
    return isoTimestamp
  }

  if (bucket === 'hour') {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(timestamp)
  }

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(timestamp)
}

const formatConversationRange = (
  range: ConversationCountReport['range'],
  bucket: ConversationReportBucket
) => {
  const start = new Date(range.start)
  const end = new Date(range.end)

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return `${range.start} – ${range.end}`
  }

  const includeYear = start.getFullYear() !== end.getFullYear()
  const dateFormatter = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: includeYear ? 'numeric' : undefined,
  })

  if (bucket === 'hour') {
    const timeFormatter = new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    })
    return `${dateFormatter.format(start)} ${timeFormatter.format(start)} – ${dateFormatter.format(end)} ${timeFormatter.format(end)}`
  }

  return `${dateFormatter.format(start)} – ${dateFormatter.format(end)}`
}

const formatPlatformLabel = (platform: string) => {
  if (!platform) {
    return 'Unknown'
  }
  return platform
    .split(/[_\s-]+/)
    .filter(part => part.length > 0)
    .map(part => part[0].toUpperCase() + part.slice(1))
    .join(' ')
}

const regionDisplayNames =
  typeof Intl.DisplayNames !== 'undefined'
    ? new Intl.DisplayNames(undefined, { type: 'region' })
    : null

const languageDisplayNames =
  typeof Intl.DisplayNames !== 'undefined'
    ? new Intl.DisplayNames(undefined, { type: 'language' })
    : null

const formatDurationFromMs = (value: number | null) => {
  if (value == null || Number.isNaN(value)) {
    return '—'
  }
  if (value < 1000) {
    return '< 1s'
  }
  const seconds = Math.round(value / 1000)
  if (seconds < 60) {
    return `${seconds}s`
  }
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  if (minutes < 60) {
    return remainingSeconds > 0
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`
  }
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
}

const formatPercentageValue = (value: number) => {
  if (!Number.isFinite(value)) {
    return '0%'
  }
  return `${value.toFixed(1)}%`
}

const normalizeLabel = (value: string | null | undefined, fallback: string) => {
  if (!value) {
    return fallback
  }
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : fallback
}

const formatCountryLabel = (country: string) => {
  const normalized = normalizeLabel(country, 'Unknown')
  if (normalized === 'Unknown') {
    return normalized
  }
  const upper = normalized.toUpperCase()
  if (upper.length === 2 && regionDisplayNames) {
    try {
      const display = regionDisplayNames.of(upper)
      if (display) {
        return display
      }
    } catch {
      // ignore lookup failures
    }
  }
  return upper
}

const formatLanguageLabel = (language: string) => {
  const normalized = normalizeLabel(language, 'Unknown').toLowerCase()
  if (normalized === 'unknown') {
    return 'Unknown'
  }
  if (languageDisplayNames) {
    try {
      const display = languageDisplayNames.of(normalized)
      if (display) {
        return display
      }
    } catch {
      // ignore lookup failures
    }
  }
  return normalized
}

const formatPendingPriorityLabel = (priority?: string | null) => {
  if (!priority) {
    return 'Pending request'
  }

  const normalized = priority.toLowerCase()
  const label = normalized.charAt(0).toUpperCase() + normalized.slice(1)
  return `${label} priority`
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const normalizeTeamIdValue = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

const REPORT_SECTIONS: ReportSection[] = [
  {
    id: 'conversationsCount',
    label: 'Conversations Count',
    description:
      'Total number of conversations handled across all channels for the selected period.',
    icon: MessageSquare,
    filterKeys: ['dateRange', 'agent', 'userType'],
    placeholder:
      'Explore conversation trends, compare periods, and track channel mix.',
  },
  {
    id: 'missedConversations',
    label: 'Missed Conversations Count',
    description:
      'Conversations that were not accepted within the team SLA window or never received an agent response.',
    icon: AlertTriangle,
    filterKeys: ['dateRange', 'agent', 'userType'],
    placeholder:
      'Review which teams breached their SLA and follow up on unresolved conversations.',
  },
  {
    id: 'averageConversationDuration',
    label: 'Average Conversation Duration',
    description:
      'Average handling time between the first and last agent responses.',
    icon: Clock,
    filterKeys: ['dateRange', 'agent'],
    placeholder:
      'Understand who is coming to the assistant by geography and language.',
  },
  {
    id: 'averageResponseTime',
    label: 'Average Agent Response Time',
    description:
      'Average time for agents to provide the first response after assignment.',
    icon: RefreshCw,
    filterKeys: ['dateRange', 'agent'],
    placeholder:
      'Track agent responsiveness and compare performance to team expectations.',
  },
  {
    id: 'agentConversationDistribution',
    label: 'Agent Conversations by Agent',
    description:
      'Conversation distribution grouped by assigned agent with volume counts.',
    icon: User,
    filterKeys: ['dateRange', 'agent'],
    placeholder:
      'Review workload distribution to balance assignments across the team.',
  },
  {
    id: 'averageAgentConversationDuration',
    label: 'Average Agent Conversations Duration',
    description:
      'Average duration of conversations handled by each agent, excluding bot-only sessions.',
    icon: Clock,
    filterKeys: ['dateRange', 'agent'],
    placeholder:
      'Compare handling times per agent to surface training or automation opportunities.',
  },
  {
    id: 'userCountries',
    label: 'User/Guest Countries',
    description:
      'Geographic distribution of users engaging with the assistant.',
    icon: Flag,
    filterKeys: ['dateRange', 'country'],
    placeholder:
      'Geo-based insights to support localisation and staffing coverage.',
  },
  {
    id: 'userLanguages',
    label: 'User Languages',
    description:
      'Language preferences detected from user profiles and sessions.',
    icon: Globe,
    filterKeys: ['dateRange', 'language'],
    placeholder:
      'Language mix to guide content translation and scripting priorities.',
  },
  {
    id: 'userBrowsers',
    label: 'User Browsers',
    description:
      'Browser technology breakdown for visitors interacting with the assistant.',
    icon: Monitor,
    filterKeys: ['dateRange', 'browser'],
    placeholder: 'Browser usage share to inform compatibility testing.',
  },
  {
    id: 'userOperatingSystems',
    label: 'User Operating Systems',
    description:
      'Operating systems used by visitors during their conversations.',
    icon: Smartphone,
    filterKeys: ['dateRange', 'os'],
    placeholder:
      'OS adoption metrics to prioritise platform optimisation plans.',
  },
]

const REPORT_FILTER_LABELS: Record<ReportFilterKey, string> = {
  dateRange: 'Date range',
  agent: 'Agent',
  userType: 'User type',
  country: 'Country',
  language: 'Language',
  browser: 'Browser',
  os: 'Operating system',
}

const REPORT_FILTER_OPTIONS: Record<
  ReportFilterKey,
  Array<{ value: string; label: string }>
> = {
  dateRange: [
    { value: 'today', label: 'Today' },
    { value: 'last1Hour', label: 'Last 1 hour' },
    { value: 'last3Hours', label: 'Last 3 hours' },
    { value: 'last24Hours', label: 'Last 24 hours' },
    { value: 'last2Days', label: 'Last 2 days' },
    { value: 'last3Days', label: 'Last 3 days' },
    { value: 'last7Days', label: 'Last 7 days' },
    { value: 'last2Weeks', label: 'Last 2 weeks' },
    { value: 'last1Month', label: 'Last 1 month' },
    { value: 'last2Months', label: 'Last 2 months' },
    { value: 'last3Months', label: 'Last 3 months' },
  ],
  agent: [
    { value: 'all', label: 'All agents' },
    { value: 'me', label: 'Only me' },
  ],
  userType: [
    { value: 'all', label: 'All visitors' },
    { value: 'registered', label: 'Registered users' },
    { value: 'guest', label: 'Guest users' },
  ],
  country: [{ value: 'all', label: 'All countries' }],
  language: [{ value: 'all', label: 'All languages' }],
  browser: [{ value: 'all', label: 'All browsers' }],
  os: [{ value: 'all', label: 'All operating systems' }],
}

const DEFAULT_REPORT_FILTERS: ReportFilterState = {
  dateRange: 'today',
  agent: 'all',
  userType: 'all',
  country: 'all',
  language: 'all',
  browser: 'all',
  os: 'all',
}

function ConversationsCountReportSection({
  report,
  isLoading,
  isFetching,
  error,
  onRetry,
  activeFilterChips,
}: ConversationsCountReportSectionProps) {
  if (isLoading) {
    return (
      <div className="flex min-h-[20rem] flex-col items-center justify-center gap-3 text-gray-600">
        <div className="flex items-center gap-2 text-sm">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-gray-300">
            <span className="h-3 w-3 animate-spin rounded-full border-2 border-blue-500 border-b-transparent" />
          </span>
          Loading conversations report…
        </div>
        <p className="text-xs text-gray-400">
          Fetching the latest conversation activity for your selected filters.
        </p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex min-h-[20rem] flex-col items-center justify-center gap-4 rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <p className="text-sm font-medium text-red-700">
          Unable to load conversations count
        </p>
        <p className="max-w-md text-xs text-red-600">
          {error.message || 'Something went wrong while loading the report.'}
        </p>
        <Button size="sm" variant="outline" onClick={onRetry}>
          Retry
        </Button>
      </div>
    )
  }

  if (!report) {
    return (
      <div className="flex min-h-[20rem] flex-col items-center justify-center gap-3 rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-600">
        <p>No report data available yet.</p>
        <p className="text-xs text-gray-400">
          Adjust the filters above to explore a different timeframe.
        </p>
      </div>
    )
  }

  const total = report.total ?? 0
  const previousTotal = report.previousTotal ?? 0
  const delta = report.delta ?? 0
  const deltaPercentage = report.deltaPercentage ?? null
  const formattedRange = formatConversationRange(report.range, report.bucket)
  const deltaLabel = delta > 0 ? `+${delta}` : `${delta}`
  const deltaPercentageLabel =
    deltaPercentage != null
      ? `${deltaPercentage > 0 ? '+' : ''}${deltaPercentage.toFixed(1)}%`
      : null
  const previousRangeLabel = formatConversationRange(
    report.previousRange,
    report.bucket
  )

  const deltaColor =
    delta > 0 ? 'text-green-600' : delta < 0 ? 'text-red-600' : 'text-gray-600'

  const hasConversationActivity = report.points.some(point => point.count > 0)

  const chartData = {
    labels: report.points.map(point =>
      formatConversationBucketLabel(point.bucket, report.bucket)
    ),
    datasets: [
      {
        label: 'Conversations',
        data: report.points.map(point => point.count),
        borderColor: '#2563EB',
        backgroundColor: 'rgba(37, 99, 235, 0.16)',
        pointBackgroundColor: '#2563EB',
        pointBorderColor: '#ffffff',
        borderWidth: 2,
        tension: 0.25,
        fill: true,
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.parsed.y ?? 0
            const suffix = value === 1 ? 'conversation' : 'conversations'
            return `${value} ${suffix}`
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          maxRotation: 0,
          autoSkip: true,
          autoSkipPadding: 12,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
        grid: {
          color: '#f1f5f9',
        },
      },
    },
  }

  const platformBreakdown = [...(report.byPlatform ?? [])].sort(
    (a, b) => b.count - a.count
  )

  const topPlatform = platformBreakdown[0]
  const maxPlatformCount =
    platformBreakdown.reduce((max, platform) => {
      return platform.count > max ? platform.count : max
    }, 0) || 1
  const bucketLabel = report.bucket === 'hour' ? 'hour' : 'day'
  const bucketLabelPlural = bucketLabel === 'hour' ? 'hours' : 'days'
  const computeFormattedCount = (value: number) =>
    Number.isInteger(value) ? value.toString() : value.toFixed(1)
  const totalBuckets = report.points.length
  const averagePerBucket =
    totalBuckets > 0 ? Number((total / totalBuckets).toFixed(1)) : 0

  let busiestPoint: ConversationCountReport['points'][number] | null = null
  let quietestPoint: ConversationCountReport['points'][number] | null = null
  for (const point of report.points) {
    if (!busiestPoint || point.count > busiestPoint.count) {
      busiestPoint = point
    }
    if (!quietestPoint || point.count < quietestPoint.count) {
      quietestPoint = point
    }
  }

  const absoluteDelta = Math.abs(delta)
  const deltaConversationLabel = `${absoluteDelta} conversation${
    absoluteDelta === 1 ? '' : 's'
  }`
  const deltaPercentageSuffix = deltaPercentageLabel
    ? ` (${deltaPercentageLabel})`
    : ''
  const trendDescription =
    delta > 0
      ? `Conversation volume increased by ${deltaConversationLabel}${deltaPercentageSuffix} compared with ${previousRangeLabel}.`
      : delta < 0
        ? `Conversation volume decreased by ${deltaConversationLabel}${deltaPercentageSuffix} compared with ${previousRangeLabel}.`
        : `Conversation volume matched the previous period (${previousRangeLabel}).`

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-gray-500">
            Total conversations
          </p>
          <p className="mt-2 text-3xl font-semibold text-gray-900">{total}</p>
          <p className="mt-2 text-xs text-gray-500">{formattedRange}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-gray-500">
            Change vs previous period
          </p>
          <div className="mt-2 flex items-baseline gap-2">
            <span className={`text-2xl font-semibold ${deltaColor}`}>
              {deltaLabel}
            </span>
            {deltaPercentageLabel && (
              <span className={`text-sm ${deltaColor}`}>
                {deltaPercentageLabel}
              </span>
            )}
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Previous period: {previousTotal}
          </p>
          <p className="mt-1 text-xs text-gray-400">
            Range: {previousRangeLabel}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-gray-500">
            Top channel
          </p>
          <p className="mt-2 text-lg font-semibold text-gray-900">
            {topPlatform
              ? `${formatPlatformLabel(topPlatform.platform)} (${topPlatform.count})`
              : 'No channel data'}
          </p>
          <p className="mt-2 text-xs text-gray-500">
            Tracking {platformBreakdown.length}{' '}
            {platformBreakdown.length === 1 ? 'platform' : 'platforms'}
          </p>
        </div>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <h4 className="text-sm font-medium text-gray-900">Key insights</h4>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Average per {bucketLabel}
            </p>
            <p className="mt-2 text-lg font-semibold text-gray-900">
              {computeFormattedCount(averagePerBucket)} conversations
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Based on {totalBuckets || 0}{' '}
              {totalBuckets === 1 ? bucketLabel : bucketLabelPlural}.
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Busiest {bucketLabel}
            </p>
            <p className="mt-2 text-lg font-semibold text-gray-900">
              {busiestPoint ? busiestPoint.count : 0} conversations
            </p>
            <p className="mt-2 text-xs text-gray-500">
              {busiestPoint
                ? formatConversationBucketLabel(
                    busiestPoint.bucket,
                    report.bucket
                  )
                : 'Waiting for activity'}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Quietest {bucketLabel}
            </p>
            <p className="mt-2 text-lg font-semibold text-gray-900">
              {quietestPoint ? quietestPoint.count : 0} conversations
            </p>
            <p className="mt-2 text-xs text-gray-500">
              {quietestPoint
                ? formatConversationBucketLabel(
                    quietestPoint.bucket,
                    report.bucket
                  )
                : 'Waiting for activity'}
            </p>
          </div>
        </div>
        <p className="mt-4 text-xs text-gray-500">{trendDescription}</p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-900">
              Conversation volume
            </h4>
            <p className="text-xs text-gray-500">
              Aggregated by {report.bucket === 'hour' ? 'hour' : 'day'}.
            </p>
          </div>
          {isFetching && (
            <div className="flex items-center gap-2 text-xs text-blue-600">
              <span className="h-3 w-3 animate-spin rounded-full border-2 border-blue-500 border-b-transparent" />
              Updating…
            </div>
          )}
        </div>

        {hasConversationActivity ? (
          <div className="h-64 w-full md:h-72">
            <Line data={chartData} options={chartOptions} />
          </div>
        ) : (
          <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-md border border-dashed border-gray-200 bg-gray-50 text-center">
            <p className="text-sm font-medium text-gray-700">
              No conversations recorded for this period
            </p>
            <p className="text-xs text-gray-500">
              Try expanding the timeframe or switching filters to see activity.
            </p>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <h4 className="text-sm font-medium text-gray-900">
          Platforms breakdown
        </h4>
        {platformBreakdown.length === 0 ? (
          <p className="mt-3 text-xs text-gray-500">
            Platform information is unavailable for this timeframe.
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            {platformBreakdown.map(platform => {
              const rawRatio =
                maxPlatformCount > 0
                  ? Math.round((platform.count / maxPlatformCount) * 100)
                  : 0
              const ratio = platform.count > 0 ? Math.max(8, rawRatio) : 0
              const totalShare =
                total > 0
                  ? Math.max(
                      0,
                      Number(((platform.count / total) * 100).toFixed(1))
                    )
                  : 0
              return (
                <div key={platform.platform || 'unknown'} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium text-gray-700">
                      {formatPlatformLabel(platform.platform)}
                    </span>
                    <span className="text-gray-500">
                      {platform.count}
                      {totalShare > 0 ? ` (${totalShare}%)` : ''}
                    </span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-gray-100">
                    <div
                      className="h-2 rounded-full bg-blue-500 transition-all"
                      style={{ width: `${ratio}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {activeFilterChips.length > 0 && (
        <div className="flex flex-wrap gap-2 text-xs text-gray-500">
          {activeFilterChips.map(filter => (
            <span
              key={`${filter.key}-${filter.value}`}
              className="rounded-full bg-gray-100 px-3 py-1"
            >
              {REPORT_FILTER_LABELS[filter.key]}: {filter.label}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function PerformanceReportSection({
  report,
  sectionId,
  isLoading,
  isFetching,
  error,
  onRetry,
  activeFilterChips,
}: PerformanceReportSectionProps) {
  if (isLoading) {
    return (
      <div className="flex min-h-[20rem] flex-col items-center justify-center gap-3 text-gray-600">
        <div className="flex items-center gap-2 text-sm">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-gray-300">
            <span className="h-3 w-3 animate-spin rounded-full border-2 border-blue-500 border-b-transparent" />
          </span>
          Loading conversation analytics…
        </div>
        <p className="text-xs text-gray-400">
          Compiling agent activity, response trends, and visitor insights for
          your filters.
        </p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex min-h-[20rem] flex-col items-center justify-center gap-4 rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <p className="text-sm font-medium text-red-700">
          Unable to load analytics
        </p>
        <p className="max-w-md text-xs text-red-600">
          {error.message ||
            'Something went wrong while loading the conversation analytics.'}
        </p>
        <Button size="sm" variant="outline" onClick={onRetry}>
          Retry
        </Button>
      </div>
    )
  }

  if (!report) {
    return (
      <div className="flex min-h-80 flex-col items-center justify-center gap-3 rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-600">
        <p>No analytics available for these filters yet.</p>
        <p className="text-xs text-gray-400">
          Try expanding the timeframe or adjusting the filters to include more
          conversations.
        </p>
      </div>
    )
  }

  const totals = report.totals
  const totalSessions = totals.sessions ?? 0
  const totalVisitors = totals.visitorCount ?? 0

  const renderAgentTable = (
    metrics: ConversationPerformanceAgentMetrics[],
    options?: {
      emptyMessage?: string
      sortComparator?: (
        a: ConversationPerformanceAgentMetrics,
        b: ConversationPerformanceAgentMetrics
      ) => number
      limit?: number
      highlightColumn?:
        | 'averageDurationMs'
        | 'averageResponseTimeMs'
        | 'missedConversations'
    }
  ) => {
    if (!metrics || metrics.length === 0) {
      return (
        <p className="mt-3 text-xs text-gray-500">
          {options?.emptyMessage ||
            'No agent data is available for these filters.'}
        </p>
      )
    }

    const sorted = [...metrics]
    if (options?.sortComparator) {
      sorted.sort(options.sortComparator)
    } else {
      sorted.sort((a, b) => b.conversationCount - a.conversationCount)
    }

    const rows = options?.limit ? sorted.slice(0, options.limit) : sorted

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th scope="col" className="px-4 py-3">
                Agent
              </th>
              <th scope="col" className="px-4 py-3">
                Conversations
              </th>
              <th scope="col" className="px-4 py-3">
                Share
              </th>
              <th scope="col" className="px-4 py-3">
                Missed
              </th>
              <th scope="col" className="px-4 py-3">
                Avg duration
              </th>
              <th scope="col" className="px-4 py-3">
                Avg response
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {rows.map(agent => {
              const share =
                totalSessions > 0
                  ? (agent.conversationCount / totalSessions) * 100
                  : 0
              const missedRate =
                agent.conversationCount > 0
                  ? (agent.missedConversations / agent.conversationCount) * 100
                  : 0
              const durationLabel = formatDurationFromMs(
                agent.averageDurationMs
              )
              const responseLabel = formatDurationFromMs(
                agent.averageResponseTimeMs
              )

              const missedHighlight =
                options?.highlightColumn === 'missedConversations'
              const durationHighlight =
                options?.highlightColumn === 'averageDurationMs'
              const responseHighlight =
                options?.highlightColumn === 'averageResponseTimeMs'

              return (
                <tr key={`${agent.agentId ?? 'unassigned'}-${agent.agentName}`}>
                  <td className="px-4 py-3 text-gray-700">{agent.agentName}</td>
                  <td className="px-4 py-3 text-gray-900">
                    {agent.conversationCount}
                  </td>
                  <td className="px-4 py-3 text-gray-500">
                    {formatPercentageValue(share)}
                  </td>
                  <td
                    className={`px-4 py-3 ${missedHighlight ? 'text-red-600' : 'text-gray-600'}`}
                  >
                    {agent.missedConversations}
                    {agent.missedConversations > 0
                      ? ` (${formatPercentageValue(missedRate)})`
                      : ''}
                  </td>
                  <td
                    className={`px-4 py-3 ${durationHighlight ? 'text-blue-600 font-medium' : 'text-gray-600'}`}
                  >
                    {durationLabel}
                  </td>
                  <td
                    className={`px-4 py-3 ${responseHighlight ? 'text-blue-600 font-medium' : 'text-gray-600'}`}
                  >
                    {responseLabel}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  }

  const renderBreakdownTable = (
    items: Array<{ label: string; count: number }>,
    total: number,
    options?: {
      emptyMessage?: string
      labelFormatter?: (label: string) => string
      limit?: number
    }
  ) => {
    if (!items || items.length === 0) {
      return (
        <p className="mt-3 text-xs text-gray-500">
          {options?.emptyMessage || 'No data available for these filters.'}
        </p>
      )
    }

    const slice = options?.limit ? items.slice(0, options.limit) : items

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th scope="col" className="px-4 py-3">
                Label
              </th>
              <th scope="col" className="px-4 py-3">
                Count
              </th>
              <th scope="col" className="px-4 py-3">
                Share
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {slice.map(item => {
              const percentage = total > 0 ? (item.count / total) * 100 : 0
              const label = options?.labelFormatter
                ? options.labelFormatter(item.label)
                : item.label
              return (
                <tr key={`${label}-${item.count}`}>
                  <td className="px-4 py-3 text-gray-700">{label}</td>
                  <td className="px-4 py-3 text-gray-900">{item.count}</td>
                  <td className="px-4 py-3 text-gray-500">
                    {formatPercentageValue(percentage)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  }

  const sections: JSX.Element[] = []

  const missedRate =
    totalSessions > 0 ? (totals.missedConversations / totalSessions) * 100 : 0

  switch (sectionId) {
    case 'missedConversations': {
      const agentsWithMissed = report.agentDistribution
        .filter(agent => agent.missedConversations > 0)
        .sort((a, b) => b.missedConversations - a.missedConversations)

      sections.push(
        <div
          key="missed-summary"
          className="grid grid-cols-1 gap-4 md:grid-cols-3"
        >
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Missed conversations
            </p>
            <p className="mt-2 text-3xl font-semibold text-gray-900">
              {totals.missedConversations}
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Across {totalSessions} conversations in this range.
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Missed conversation rate
            </p>
            <p className="mt-2 text-3xl font-semibold text-gray-900">
              {formatPercentageValue(missedRate)}
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Percentage of sessions without an agent response.
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Response readiness
            </p>
            <p className="mt-2 text-lg font-semibold text-gray-900">
              Average response:{' '}
              {formatDurationFromMs(totals.averageResponseTimeMs)}
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Reduce missed sessions by focusing on slower response windows.
            </p>
          </div>
        </div>
      )

      sections.push(
        <div
          key="missed-agents"
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-900">
                Agents with missed conversations
              </h4>
              <p className="text-xs text-gray-500">
                Focus on agents or teams with higher missed counts to review
                escalation workflows.
              </p>
            </div>
          </div>
          {renderAgentTable(agentsWithMissed, {
            emptyMessage:
              'Great news — no missed conversations for these filters.',
            highlightColumn: 'missedConversations',
          })}
        </div>
      )
      break
    }

    case 'averageConversationDuration': {
      sections.push(
        <div
          key="duration-cards"
          className="grid grid-cols-1 gap-4 md:grid-cols-3"
        >
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Average duration
            </p>
            <p className="mt-2 text-3xl font-semibold text-gray-900">
              {formatDurationFromMs(totals.averageConversationDurationMs)}
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Mean time from first to last message per session.
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Median duration
            </p>
            <p className="mt-2 text-3xl font-semibold text-gray-900">
              {formatDurationFromMs(totals.medianConversationDurationMs)}
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Half of conversations finish within this time.
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Visitor count (unique)
            </p>
            <p className="mt-2 text-3xl font-semibold text-gray-900">
              {totalVisitors}
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Unique visitors engaging during this timeframe.
            </p>
          </div>
        </div>
      )

      sections.push(
        <div
          key="duration-distribution"
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          <h4 className="text-sm font-medium text-gray-900">
            Duration distribution
          </h4>
          <p className="text-xs text-gray-500">
            Understand how long conversations typically take to resolve.
          </p>
          <div className="mt-4">
            {renderBreakdownTable(
              report.durationDistribution.map(item => ({
                label: item.label,
                count: item.count,
              })),
              totalSessions,
              {
                emptyMessage:
                  'No completed conversations for these filters yet.',
              }
            )}
          </div>
        </div>
      )

      const topByDuration = [...report.agentDistribution]
        .filter(agent => agent.averageDurationMs != null)
        .sort((a, b) => (b.averageDurationMs ?? 0) - (a.averageDurationMs ?? 0))

      sections.push(
        <div
          key="duration-agents"
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          <h4 className="text-sm font-medium text-gray-900">
            Agents by average conversation duration
          </h4>
          <p className="text-xs text-gray-500">
            Identify where longer engagements occur to optimise handover or
            knowledge resources.
          </p>
          {renderAgentTable(topByDuration, {
            limit: 8,
            highlightColumn: 'averageDurationMs',
            emptyMessage:
              'No agent-managed conversations for these filters yet.',
          })}
        </div>
      )
      break
    }

    case 'visitorCount': {
      sections.push(
        <div
          key="visitor-summary"
          className="grid grid-cols-1 gap-4 md:grid-cols-2"
        >
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Unique visitors
            </p>
            <p className="mt-2 text-3xl font-semibold text-gray-900">
              {totalVisitors}
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Total unique visitors engaging during this period.
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Sessions observed
            </p>
            <p className="mt-2 text-3xl font-semibold text-gray-900">
              {totalSessions}
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Conversations tracked across all channels.
            </p>
          </div>
        </div>
      )

      sections.push(
        <div
          key="visitor-countries"
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          <h4 className="text-sm font-medium text-gray-900">Top countries</h4>
          <p className="text-xs text-gray-500">
            Where your visitors are joining from.
          </p>
          <div className="mt-4">
            {renderBreakdownTable(
              report.visitorsByCountry.map(item => ({
                label: item.country,
                count: item.count,
              })),
              totalVisitors,
              {
                labelFormatter: formatCountryLabel,
                emptyMessage:
                  'No country information available for these conversations.',
                limit: 8,
              }
            )}
          </div>
        </div>
      )

      sections.push(
        <div
          key="visitor-languages"
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          <h4 className="text-sm font-medium text-gray-900">
            Preferred languages
          </h4>
          <p className="text-xs text-gray-500">
            Detected language hints from browser and session metadata.
          </p>
          <div className="mt-4">
            {renderBreakdownTable(
              report.visitorsByLanguage.map(item => ({
                label: item.language,
                count: item.count,
              })),
              totalVisitors,
              {
                labelFormatter: formatLanguageLabel,
                emptyMessage:
                  'No language preferences detected for these conversations.',
                limit: 8,
              }
            )}
          </div>
        </div>
      )
      break
    }

    case 'averageResponseTime': {
      sections.push(
        <div
          key="response-summary"
          className="grid grid-cols-1 gap-4 md:grid-cols-3"
        >
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Average response time
            </p>
            <p className="mt-2 text-3xl font-semibold text-gray-900">
              {formatDurationFromMs(totals.averageResponseTimeMs)}
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Time from first visitor message to first agent reply.
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Median response
            </p>
            <p className="mt-2 text-3xl font-semibold text-gray-900">
              {formatDurationFromMs(totals.medianResponseTimeMs)}
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Half of the responses arrive within this timeframe.
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Missed conversations
            </p>
            <p className="mt-2 text-3xl font-semibold text-gray-900">
              {totals.missedConversations}
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Work with high-response-time agents to reduce missed sessions.
            </p>
          </div>
        </div>
      )

      const fastestAgents = [...report.agentDistribution]
        .filter(agent => agent.averageResponseTimeMs != null)
        .sort(
          (a, b) =>
            (a.averageResponseTimeMs ?? Infinity) -
            (b.averageResponseTimeMs ?? Infinity)
        )

      sections.push(
        <div
          key="response-agents"
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          <h4 className="text-sm font-medium text-gray-900">
            Fastest agent responses
          </h4>
          <p className="text-xs text-gray-500">
            Highlight agents and teams delivering the quickest first responses.
          </p>
          {renderAgentTable(fastestAgents, {
            limit: 8,
            highlightColumn: 'averageResponseTimeMs',
            emptyMessage: 'No agent responses recorded for these filters yet.',
          })}
        </div>
      )
      break
    }

    case 'agentConversationDistribution': {
      sections.push(
        <div
          key="agent-distribution"
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          <h4 className="text-sm font-medium text-gray-900">
            Agent conversation distribution
          </h4>
          <p className="text-xs text-gray-500">
            Review workload balance and identify who is handling the majority of
            conversations.
          </p>
          {renderAgentTable(report.agentDistribution, {
            emptyMessage: 'No agent-managed conversations for these filters.',
          })}
        </div>
      )
      break
    }

    case 'averageAgentConversationDuration': {
      const longestDurations = [...report.agentDistribution]
        .filter(agent => agent.averageDurationMs != null)
        .sort((a, b) => (b.averageDurationMs ?? 0) - (a.averageDurationMs ?? 0))

      sections.push(
        <div
          key="agent-duration"
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          <h4 className="text-sm font-medium text-gray-900">
            Average duration by agent
          </h4>
          <p className="text-xs text-gray-500">
            Spot longer-running conversations and provide coaching or knowledge
            resources.
          </p>
          {renderAgentTable(longestDurations, {
            highlightColumn: 'averageDurationMs',
            emptyMessage: 'No agent-managed conversations for these filters.',
          })}
        </div>
      )
      break
    }

    case 'userCountries': {
      sections.push(
        <div
          key="breakdown-countries"
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          <h4 className="text-sm font-medium text-gray-900">
            Visitor countries
          </h4>
          <p className="text-xs text-gray-500">
            Geo-distribution based on session metadata and network headers.
          </p>
          <div className="mt-4">
            {renderBreakdownTable(
              report.visitorsByCountry.map(item => ({
                label: item.country,
                count: item.count,
              })),
              totalVisitors,
              {
                labelFormatter: formatCountryLabel,
                emptyMessage:
                  'Country metadata is not available for these conversations.',
              }
            )}
          </div>
        </div>
      )
      break
    }

    case 'userLanguages': {
      sections.push(
        <div
          key="breakdown-languages"
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          <h4 className="text-sm font-medium text-gray-900">
            Visitor languages
          </h4>
          <p className="text-xs text-gray-500">
            Use this to prioritise multilingual scripts and templates.
          </p>
          <div className="mt-4">
            {renderBreakdownTable(
              report.visitorsByLanguage.map(item => ({
                label: item.language,
                count: item.count,
              })),
              totalVisitors,
              {
                labelFormatter: formatLanguageLabel,
                emptyMessage:
                  'Language preferences were not captured for these conversations.',
              }
            )}
          </div>
        </div>
      )
      break
    }

    case 'userBrowsers': {
      sections.push(
        <div
          key="breakdown-browsers"
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          <h4 className="text-sm font-medium text-gray-900">
            Visitor browsers
          </h4>
          <p className="text-xs text-gray-500">
            Validate compatibility for the browsers most frequently used by
            visitors.
          </p>
          <div className="mt-4">
            {renderBreakdownTable(
              report.visitorsByBrowser.map(item => ({
                label: item.browser,
                count: item.count,
              })),
              totalVisitors,
              {
                labelFormatter: formatPlatformLabel,
                emptyMessage:
                  'Browser metadata is unavailable for these visitors.',
              }
            )}
          </div>
        </div>
      )
      break
    }

    case 'userOperatingSystems': {
      sections.push(
        <div
          key="breakdown-os"
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          <h4 className="text-sm font-medium text-gray-900">
            Visitor operating systems
          </h4>
          <p className="text-xs text-gray-500">
            Optimise device-specific experiences by focusing on the most common
            operating systems.
          </p>
          <div className="mt-4">
            {renderBreakdownTable(
              report.visitorsByOperatingSystem.map(item => ({
                label: item.os,
                count: item.count,
              })),
              totalVisitors,
              {
                labelFormatter: formatPlatformLabel,
                emptyMessage:
                  'Operating system metadata is unavailable for these visitors.',
              }
            )}
          </div>
        </div>
      )
      break
    }

    default:
      break
  }

  return (
    <div className="flex flex-col gap-6">
      {isFetching && (
        <div className="ml-auto flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs text-blue-600">
          <span className="h-2.5 w-2.5 animate-spin rounded-full border-2 border-blue-500 border-b-transparent" />
          Updating…
        </div>
      )}

      {sections}

      {activeFilterChips.length > 0 && (
        <div className="flex flex-wrap gap-2 text-xs text-gray-500">
          {activeFilterChips.map(filter => (
            <span
              key={`${filter.key}-${filter.value}`}
              className="rounded-full bg-gray-100 px-3 py-1"
            >
              {REPORT_FILTER_LABELS[filter.key]}: {filter.label}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

type TakeoverActionType =
  | 'takeover'
  | 'force_takeover'
  | 'assign_agent'
  | 'assign_team'

type TakeoverResult = {
  success: boolean
  message?: string
}

type DashboardTabValue =
  | 'overview'
  | 'chat'
  | 'my'
  | 'all'
  | 'recent'
  | 'reports'
  | 'articles'
  | 'tickets'

const DASHBOARD_TAB_VALUES: DashboardTabValue[] = [
  'overview',
  'chat',
  'my',
  'all',
  'recent',
  'reports',
  'articles',
  'tickets',
  'articles',
  'reports',
]

// Restrict default dashboard tab explicitly to a value supported by AgentChatStore
const DEFAULT_DASHBOARD_TAB = 'overview' as const

const isDashboardTabValue = (value: string): value is DashboardTabValue => {
  return DASHBOARD_TAB_VALUES.includes(value as DashboardTabValue)
}

function EmbeddedAgentChat({ sessionId }: { sessionId: string }) {
  const setActiveSessionId = useAgentChatStore(
    state => state.setActiveSessionId
  )
  const setActiveDashboardTab = useAgentChatStore(
    state => state.setActiveDashboardTab
  )

  useEffect(() => {
    setActiveSessionId(sessionId)
    setActiveDashboardTab('chat')
  }, [sessionId, setActiveDashboardTab, setActiveSessionId])

  return <AgentChatPanel key={sessionId} sessionId={sessionId} />
}

export default function AgentDashboard() {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const {
    user,
    token,
    logout,
    verify,
    isAuthenticated,
    isLoading: authIsLoading,
  } = useAgentAuth()

  const permissionSet = useMemo(
    () => buildPermissionSet((user as any)?.permissions),
    [user]
  )
  const canManageHandovers = useMemo(
    () => hasPermission(permissionSet, 'CHAT_TRANSFER'),
    [permissionSet]
  )
  const canManageTeamSetup = useMemo(
    () => hasPermission(permissionSet, 'TEAM_SETUP'),
    [permissionSet]
  )
  const notifications = useNotifications()
  const notificationActions = useNotificationActions()
  const { teamFilter, fetchTeams, isLoading: teamsAreLoading } = useTeamsStore()
  const availableTeams = useMemo(
    () => teamFilter.availableTeams ?? [],
    [teamFilter.availableTeams]
  )
  const getTeamNameById = useTeamNameById()

  // Filter teams to only show teams the agent is assigned to
  const agentTeams = useMemo(() => {
    const userTeamIds = user?.teamIds ?? []
    if (userTeamIds.length === 0) {
      return availableTeams
    }
    return availableTeams.filter(team => userTeamIds.includes(team.id))
  }, [availableTeams, user])

  const userTeamIdList = useMemo(() => {
    const ids = user?.teamIds ?? []
    if (!Array.isArray(ids)) {
      return [] as number[]
    }
    return ids
      .map(id => {
        const parsed = Number.parseInt(String(id), 10)
        return Number.isFinite(parsed) ? parsed : null
      })
      .filter((id): id is number => id != null)
  }, [user?.teamIds])

  const resolvedAgentId =
    user?.id != null && !Number.isNaN(Number(user.id))
      ? Number(user.id)
      : undefined

  const agentRoles = useMemo(() => extractUserRoles(user), [user])
  const primaryRole = getPrimaryRole(user)
  const agentRoleLabel = agentRoles.length ? agentRoles.join(', ') : 'UNKNOWN'
  const isSupervisor = agentRoles.includes(UserRole.SUPERVISOR)
  // Debug: Log permission status
  useEffect(() => {
    console.log('🔐 Permission Debug:', {
      user,
      permissions: user?.permissions,
      permissionSet,
      canManageTeamSetup,
      primaryRole,
      agentRoles,
    })
  }, [user, permissionSet, canManageTeamSetup, primaryRole, agentRoles])
  const agentTeamNames = useMemo(() => {
    const names = agentTeams
      .map(team => team.name?.trim())
      .filter((name): name is string => Boolean(name))
    return Array.from(new Set(names))
  }, [agentTeams])

  // Debug logging to understand role detection
  useEffect(() => {
    console.log('Dashboard Debug:', {
      user,
      agentRoles,
      primaryRole,
      agentRoleLabel,
      isSupervisor,
      supervisorValue: UserRole.SUPERVISOR,
    })
  }, [user, agentRoles, primaryRole, agentRoleLabel, isSupervisor])

  const [isOnline, setIsOnline] = useState(false)
  const [isHydrated, setIsHydrated] = useState(false)
  const [isMobileViewport, setIsMobileViewport] = useState(false)
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false)

  const queryClient = useQueryClient()

  // Set isHydrated to true after component mounts to avoid hydration mismatches
  useEffect(() => {
    setIsHydrated(true)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const updateViewport = () => {
      setIsMobileViewport(window.innerWidth < 1024)
    }

    updateViewport()
    window.addEventListener('resize', updateViewport)
    return () => {
      window.removeEventListener('resize', updateViewport)
    }
  }, [])

  useEffect(() => {
    if (teamFilter.availableTeams.length === 0) {
      fetchTeams().catch(error => {
        console.error('Failed to fetch teams for agent dashboard:', error)
      })
    }
  }, [fetchTeams, teamFilter.availableTeams.length])

  const activeDashboardTab = useAgentChatStore(
    state => state.activeDashboardTab
  )
  const setActiveDashboardTab = useAgentChatStore(
    state => state.setActiveDashboardTab
  )
  const setActiveSessionId = useAgentChatStore(
    state => state.setActiveSessionId
  )
  const activeChatSessionId = useAgentChatStore(state => state.activeSessionId)
  const { agentSidebarWidth, setAgentSidebarWidth, setSidebarCollapsed } =
    useSidebarState()

  useEffect(() => {
    if (!isHydrated) {
      return
    }

    if (isMobileViewport) {
      setSidebarCollapsed(true)
      return
    }

    setSidebarCollapsed(false)
    setIsMobileSidebarOpen(false)
  }, [
    isHydrated,
    isMobileViewport,
    setSidebarCollapsed,
    setIsMobileSidebarOpen,
  ])
  const normalizedDashboardTab = useMemo(() => {
    if (
      activeDashboardTab === 'chat' ||
      activeDashboardTab === 'reports' ||
      activeDashboardTab === 'articles' ||
      activeDashboardTab === 'tickets'
    ) {
      return activeDashboardTab
    }
    return null
  }, [activeDashboardTab])

  const sidebarRef = useRef<HTMLDivElement | null>(null)
  const sidebarResizeState = useRef<{
    startX: number
    startWidth: number
  } | null>(null)
  const [isResizingSidebar, setIsResizingSidebar] = useState(false)
  const [activeSessionsTab, setActiveSessionsTab] = useState<DashboardTabValue>(
    // Use DEFAULT_DASHBOARD_TAB during SSR to prevent hydration mismatch
    isHydrated
      ? normalizedDashboardTab || DEFAULT_DASHBOARD_TAB
      : DEFAULT_DASHBOARD_TAB
  )

  const [isMainSidebarCollapsed, setIsMainSidebarCollapsed] = useState(false)

  const computedSidebarStyle: CSSProperties | undefined = useMemo(() => {
    if (isMobileViewport) {
      return undefined
    }
    // Default width if agentSidebarWidth is missing? Assuming agentSidebarWidth is always defined or has a default in store.
    const width = isMainSidebarCollapsed ? 0 : agentSidebarWidth
    return {
      width,
      transition: 'width 0.3s ease-in-out, opacity 0.3s ease-in-out',
      opacity: isMainSidebarCollapsed ? 0 : 1,
      overflow: 'hidden',
      // Ensure we don't take up space when widths are 0
      marginRight: isMainSidebarCollapsed ? 0 : undefined,
    }
  }, [isMobileViewport, isMainSidebarCollapsed, agentSidebarWidth])

  // Auto-collapse sidebar when on tickets tab
  useEffect(() => {
    if (activeSessionsTab === 'tickets') {
      setIsMainSidebarCollapsed(true)
    } else {
      setIsMainSidebarCollapsed(false)
    }
  }, [activeSessionsTab])

  // Sync activeSessionsTab with store after hydration
  useEffect(() => {
    if (!isHydrated) {
      return
    }

    if (
      normalizedDashboardTab &&
      normalizedDashboardTab !== activeSessionsTab
    ) {
      setActiveSessionsTab(normalizedDashboardTab)
    }
  }, [
    isHydrated,
    normalizedDashboardTab,
    activeSessionsTab,
    setActiveSessionsTab,
  ])

  useEffect(() => {
    if (!canManageHandovers && activeSessionsTab === 'recent') {
      setActiveSessionsTab('overview')
      setActiveDashboardTab('overview')
    }
  }, [
    canManageHandovers,
    activeSessionsTab,
    setActiveSessionsTab,
    setActiveDashboardTab,
  ])

  const pendingTabRef = useRef<DashboardTabValue | null>(null)
  const isInternalTabUpdateRef = useRef(false)
  const isInternalSessionUpdateRef = useRef(false)
  const takeoverInFlightRef = useRef<Set<string>>(new Set())
  const updateUrlWithTab = useCallback(
    (tab: DashboardTabValue, options?: { forceTabParam?: boolean }) => {
      if (!pathname) {
        return
      }

      const isDefaultTab = tab === DEFAULT_DASHBOARD_TAB
      const currentTabParam = searchParams.get('tab')

      if (options?.forceTabParam) {
        if (currentTabParam === tab) {
          return
        }
      } else {
        if (!isDefaultTab && currentTabParam === tab) {
          return
        }

        if (isDefaultTab && currentTabParam === null) {
          return
        }
      }

      const params = new URLSearchParams(searchParams.toString())

      if (isDefaultTab && !options?.forceTabParam) {
        params.delete('tab')
      } else {
        params.set('tab', tab)
      }

      if (tab !== 'chat') {
        params.delete('session')
        params.delete('sessionId')
      }

      const nextQuery = params.toString()
      const currentQuery = searchParams.toString()
      if (nextQuery === currentQuery) {
        return
      }

      isInternalTabUpdateRef.current = true
      router.replace(nextQuery ? `${pathname}?${nextQuery}` : pathname)
    },
    [pathname, router, searchParams]
  )

  const updateUrlForChatSession = useCallback(
    (sessionId: string | null, options?: { ensureTab?: boolean }) => {
      if (!pathname) {
        return
      }

      const params = new URLSearchParams(searchParams.toString())

      if (options?.ensureTab) {
        params.set('tab', 'chat')
      }

      params.delete('sessionId')

      if (sessionId) {
        params.set('session', sessionId)
      } else {
        params.delete('session')
      }

      const nextQuery = params.toString()
      const currentQuery = searchParams.toString()
      if (nextQuery === currentQuery) {
        return
      }

      isInternalTabUpdateRef.current = true
      isInternalSessionUpdateRef.current = true
      router.replace(nextQuery ? `${pathname}?${nextQuery}` : pathname)
    },
    [pathname, router, searchParams]
  )

  const applyTabSelection = useCallback(
    (
      tab: DashboardTabValue,
      options?: { skipUrlUpdate?: boolean; forceDefaultParam?: boolean }
    ) => {
      setActiveSessionsTab(current => {
        if (current === tab) {
          return current
        }
        return tab
      })

      if (
        tab === 'chat' ||
        tab === 'reports' ||
        tab === 'articles' ||
        tab === 'tickets'
      ) {
        if (activeDashboardTab !== tab) {
          setActiveDashboardTab(tab)
        }
      } else if (activeDashboardTab !== 'overview') {
        setActiveDashboardTab('overview')
      }

      if (tab !== 'chat' && activeChatSessionId) {
        isInternalSessionUpdateRef.current = true
        setActiveSessionId(null)
      }

      if (!options?.skipUrlUpdate) {
        updateUrlWithTab(tab, {
          forceTabParam:
            options?.forceDefaultParam && tab === DEFAULT_DASHBOARD_TAB,
        })
      }
    },
    [
      activeChatSessionId,
      activeDashboardTab,
      setActiveDashboardTab,
      setActiveSessionId,
      updateUrlWithTab,
    ]
  )

  // Helper to open a chat session and navigate to the Chat tab
  const openChatSession = useCallback(
    (sessionId: string) => {
      if (!sessionId) {
        return
      }
      setActiveSessionId(sessionId)
      // Switch tab locally and update URL to canonical form
      applyTabSelection('chat', { skipUrlUpdate: true })
      updateUrlForChatSession(sessionId, { ensureTab: true })
    },
    [setActiveSessionId, applyTabSelection, updateUrlForChatSession]
  )

  const handleMobileSessionSelect = useCallback(
    (sessionId: string) => {
      openChatSession(String(sessionId))
      setIsMobileSidebarOpen(false)
    },
    [openChatSession, setIsMobileSidebarOpen]
  )

  const [isSupervisorModalOpen, setIsSupervisorModalOpen] = useState(false)
  const [supervisorSessionId, setSupervisorSessionId] = useState<string | null>(
    null
  )
  const [isSupervisorActionLoading, setIsSupervisorActionLoading] =
    useState(false)

  // Takeover team selection modal states
  const {
    isOpen: showTakeoverTeamModal,
    selectedTeamId: selectedTakeoverTeamId,
    pendingSessionId: pendingTakeoverSessionId,
    pendingOptions: pendingTakeoverOptions,
    pendingAcceptRequestId,
    isSubmitting: isTakeoverTeamSubmitting,
    openForSession: openTakeoverTeamModalForSession,
    openForAccept: openTakeoverTeamModalForAccept,
    close: closeTakeoverTeamModal,
    setSelectedTeamId: setSelectedTakeoverTeamId,
    setSubmitting: setIsTakeoverTeamSubmitting,
  } = useTakeoverTeamModal()

  const [activeReportId, setActiveReportId] =
    useState<ReportSectionId>('conversationsCount')
  const [reportFilters, setReportFilters] = useState<ReportFilterState>(() => ({
    ...DEFAULT_REPORT_FILTERS,
  }))

  const selectedReportSection = useMemo(() => {
    return (
      REPORT_SECTIONS.find(section => section.id === activeReportId) ??
      REPORT_SECTIONS[0]
    )
  }, [activeReportId])

  const visibleReportFilters = useMemo(
    () => selectedReportSection.filterKeys,
    [selectedReportSection]
  )

  const handleReportFilterChange = useCallback(
    (key: ReportFilterKey, value: string) => {
      setReportFilters(prev => ({
        ...prev,
        [key]: value,
      }))
    },
    []
  )

  const resetReportFilters = useCallback(() => {
    setReportFilters({ ...DEFAULT_REPORT_FILTERS })
  }, [])

  const getReportFilterValueLabel = useCallback(
    (key: ReportFilterKey, value: string) => {
      const option = REPORT_FILTER_OPTIONS[key].find(
        item => item.value === value
      )
      return option?.label ?? value
    },
    []
  )

  const appliedReportFilters = useMemo(
    () =>
      visibleReportFilters.map(filterKey => ({
        key: filterKey,
        value: reportFilters[filterKey],
        label: getReportFilterValueLabel(filterKey, reportFilters[filterKey]),
      })),
    [getReportFilterValueLabel, reportFilters, visibleReportFilters]
  )

  const activeReportFilterChips = useMemo(
    () =>
      appliedReportFilters.filter(filter => {
        if (filter.key === 'dateRange') {
          return true
        }
        return filter.value !== DEFAULT_REPORT_FILTERS[filter.key]
      }),
    [appliedReportFilters]
  )

  const hasReportFilterOverrides = useMemo(
    () =>
      visibleReportFilters.some(
        filterKey =>
          reportFilters[filterKey] !== DEFAULT_REPORT_FILTERS[filterKey]
      ),
    [reportFilters, visibleReportFilters]
  )

  const sidebarStyle = useMemo<CSSProperties>(
    () => ({
      width: `min(100%, ${Math.max(agentSidebarWidth, 120)}px)`,
      minWidth: 120,
    }),
    [agentSidebarWidth]
  )

  const handleSidebarPointerMove = useCallback(
    (event: PointerEvent) => {
      if (!sidebarResizeState.current) {
        return
      }

      const deltaX = event.clientX - sidebarResizeState.current.startX
      const nextWidth = sidebarResizeState.current.startWidth + deltaX
      setAgentSidebarWidth(nextWidth)
    },
    [setAgentSidebarWidth]
  )

  const handleSidebarPointerUp = useCallback(() => {
    if (!sidebarResizeState.current) {
      return
    }

    sidebarResizeState.current = null
    setIsResizingSidebar(false)
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
    window.removeEventListener('pointermove', handleSidebarPointerMove)
    window.removeEventListener('pointerup', handleSidebarPointerUp)
  }, [handleSidebarPointerMove])

  const handleSidebarPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (isMobileViewport || !sidebarRef.current) {
        return
      }

      event.preventDefault()
      sidebarResizeState.current = {
        startX: event.clientX,
        startWidth: sidebarRef.current.getBoundingClientRect().width,
      }

      setIsResizingSidebar(true)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      window.addEventListener('pointermove', handleSidebarPointerMove)
      window.addEventListener('pointerup', handleSidebarPointerUp)
    },
    [handleSidebarPointerMove, handleSidebarPointerUp, isMobileViewport]
  )

  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', handleSidebarPointerMove)
      window.removeEventListener('pointerup', handleSidebarPointerUp)
    }
  }, [handleSidebarPointerMove, handleSidebarPointerUp])

  // Guarded tab change handler to prevent redundant selection churn
  const handleTabChange = useCallback(
    (value: string) => {
      if (!isDashboardTabValue(value)) {
        return
      }
      // Avoid re-applying the same tab or applying while an internal update is in flight
      if (value === activeSessionsTab) {
        return
      }
      if (pendingTabRef.current === value) {
        return
      }
      pendingTabRef.current = value
      applyTabSelection(value, {
        forceDefaultParam: value === DEFAULT_DASHBOARD_TAB,
      })
    },
    [applyTabSelection, activeSessionsTab]
  )

  useEffect(() => {
    if (pendingTabRef.current === activeSessionsTab) {
      pendingTabRef.current = null
    }
  }, [activeSessionsTab])

  useEffect(() => {
    if (activeDashboardTab === 'chat' && activeSessionsTab !== 'chat') {
      applyTabSelection('chat')
      return
    }

    if (activeDashboardTab === 'reports' && activeSessionsTab !== 'reports') {
      applyTabSelection('reports')
      return
    }

    if (activeDashboardTab === 'articles' && activeSessionsTab !== 'articles') {
      applyTabSelection('articles')
      return
    }

    if (activeDashboardTab === 'tickets' && activeSessionsTab !== 'tickets') {
      applyTabSelection('tickets')
      return
    }

    if (
      activeDashboardTab === 'overview' &&
      (activeSessionsTab === 'chat' ||
        activeSessionsTab === 'reports' ||
        activeSessionsTab === 'articles' ||
        activeSessionsTab === 'tickets')
    ) {
      applyTabSelection('overview')
    }
  }, [activeDashboardTab, activeSessionsTab, applyTabSelection])

  useEffect(() => {
    if (isInternalTabUpdateRef.current) {
      isInternalTabUpdateRef.current = false
      return
    }

    const tabParam = searchParams.get('tab')

    if (tabParam == null) {
      updateUrlWithTab(activeSessionsTab, {
        forceTabParam: activeSessionsTab === DEFAULT_DASHBOARD_TAB,
      })
      return
    }

    if (!isDashboardTabValue(tabParam)) {
      applyTabSelection(DEFAULT_DASHBOARD_TAB, { skipUrlUpdate: true })
      updateUrlWithTab(DEFAULT_DASHBOARD_TAB, { forceTabParam: true })
      return
    }

    if (tabParam !== activeSessionsTab) {
      if (
        tabParam === DEFAULT_DASHBOARD_TAB &&
        pendingTabRef.current != null &&
        pendingTabRef.current !== tabParam
      ) {
        // Skip reverting to the default tab while a new selection is still being applied.
        return
      }
      applyTabSelection(tabParam, { skipUrlUpdate: true })
      return
    }

    if (tabParam === DEFAULT_DASHBOARD_TAB) {
      updateUrlWithTab(DEFAULT_DASHBOARD_TAB, { forceTabParam: true })
    }
  }, [activeSessionsTab, applyTabSelection, searchParams, updateUrlWithTab])

  useEffect(() => {
    const sessionParam =
      searchParams.get('session') ?? searchParams.get('sessionId')

    if (isInternalSessionUpdateRef.current) {
      isInternalSessionUpdateRef.current = false
      return
    }

    if (sessionParam) {
      if (sessionParam !== activeChatSessionId) {
        setActiveSessionId(sessionParam)
      }

      if (activeSessionsTab !== 'chat') {
        applyTabSelection('chat', { skipUrlUpdate: true })
      }

      if (activeDashboardTab !== 'chat') {
        setActiveDashboardTab('chat')
      }

      // Canonicalize URL to 'session' and ensure 'tab=chat'
      if (searchParams.get('tab') !== 'chat' || searchParams.get('sessionId')) {
        updateUrlForChatSession(sessionParam, { ensureTab: true })
      }

      return
    }

    if (!sessionParam && activeChatSessionId) {
      setActiveSessionId(null)
    }
  }, [
    activeChatSessionId,
    activeDashboardTab,
    activeSessionsTab,
    applyTabSelection,
    searchParams,
    setActiveDashboardTab,
    setActiveSessionId,
    updateUrlForChatSession,
  ])

  const selectedTeamIdParam =
    !teamFilter.showAllTeams && teamFilter.selectedTeamId !== null
      ? (teamFilter.selectedTeamId ?? undefined)
      : undefined
  const teamKey = !teamFilter.showAllTeams
    ? String(teamFilter.selectedTeamId ?? 'none')
    : 'all'

  const canLoadDashboard = isAuthenticated && !authIsLoading

  const defaultStats: DashboardStats = {
    totalSessions: 0,
    activeSessions: 0,
    pendingHandovers: 0,
    completedToday: 0,
  }

  const statsQuery = useQuery<DashboardStats, Error>({
    queryKey: ['agent-dashboard', teamKey, 'stats'],
    enabled: canLoadDashboard,
    staleTime: 15_000,
    queryFn: async () => {
      const response = await api.sessions.getStats(selectedTeamIdParam)
      if (!response.success) {
        throw new Error(response.error || 'Failed to load stats')
      }
      return response.data ?? defaultStats
    },
  })

  useEffect(() => {
    if (statsQuery.error) {
      console.error('Failed to load stats:', statsQuery.error)
    }
  }, [statsQuery.error])

  const handoverQueryEnabled = canLoadDashboard && canManageHandovers

  const handoversQuery = useQuery<HandoverRequest[], Error>({
    queryKey: ['agent-dashboard', teamKey, 'handovers'],
    enabled: handoverQueryEnabled,
    staleTime: 10_000,
    queryFn: async () => {
      const response =
        await api.sessions.getPendingHandovers(selectedTeamIdParam)
      if (!response.success) {
        throw new Error(response.error || 'Failed to load handover data')
      }
      return Array.isArray(response.data) ? response.data : []
    },
  })

  useEffect(() => {
    if (!handoverQueryEnabled) {
      return
    }
    if (handoversQuery.error) {
      console.error('Failed to load handover data:', handoversQuery.error)
    }
  }, [handoverQueryEnabled, handoversQuery.error])

  const activeSessionsQuery = useQuery<ActiveSession[], Error>({
    queryKey: ['agent-dashboard', teamKey, 'active-sessions'],
    enabled: canLoadDashboard,
    staleTime: 10_000,
    queryFn: async () => {
      const response = await api.sessions.getActiveSessions(selectedTeamIdParam)
      if (!response.success) {
        throw new Error(response.error || 'Failed to load active sessions')
      }
      const activeData = Array.isArray(response.data)
        ? (response.data as unknown as ActiveSession[])
        : []
      return activeData.reduce(
        (unique: ActiveSession[], session: ActiveSession) => {
          const existingIndex = unique.findIndex(s => s.id === session.id)
          if (existingIndex >= 0) {
            const existing = unique[existingIndex]
            const sessionTime = session.lastMessageAt || session.createdAt
            const existingTime = existing.lastMessageAt || existing.createdAt
            if (new Date(sessionTime) > new Date(existingTime)) {
              unique[existingIndex] = session
            }
          } else {
            unique.push(session)
          }
          return unique
        },
        [] as ActiveSession[]
      )
    },
  })

  useEffect(() => {
    if (activeSessionsQuery.error) {
      console.error(
        'Failed to load active sessions:',
        activeSessionsQuery.error
      )
    }
  }, [activeSessionsQuery.error])

  const allActiveSessionsQuery = useQuery<AllActiveSession[], Error>({
    queryKey: ['agent-dashboard', teamKey, 'all-active-sessions'],
    enabled: canLoadDashboard,
    staleTime: 10_000,
    queryFn: async () => {
      const response =
        await api.sessions.getAllActiveSessions(selectedTeamIdParam)
      if (!response.success) {
        throw new Error(response.error || 'Failed to load all active sessions')
      }
      return Array.isArray(response.data)
        ? (response.data as unknown as AllActiveSession[])
        : []
    },
  })

  useEffect(() => {
    if (allActiveSessionsQuery.error) {
      console.error(
        'Failed to load all active sessions:',
        allActiveSessionsQuery.error
      )
    }
  }, [allActiveSessionsQuery.error])

  const visitorsQuery = useQuery<Visitor[], Error>({
    queryKey: ['agent-dashboard', teamKey, 'visitors'],
    enabled: canLoadDashboard,
    staleTime: 10_000,
    queryFn: async () => {
      const response = await api.sessions.getVisitors(selectedTeamIdParam)
      if (!response.success) {
        throw new Error(response.error || 'Failed to load visitors')
      }
      const data = Array.isArray(response.data) ? response.data : []
      return data.map(visitor => ({
        ...visitor,
        isActive: true, // Default to active since API doesn't provide this field
      }))
    },
  })

  const isConversationsReportSelected =
    selectedReportSection.id === 'conversationsCount'
  const isPerformanceReportSelected =
    selectedReportSection.id !== 'conversationsCount'

  const conversationsCountQuery = useQuery<ConversationCountReport, Error>({
    queryKey: [
      'agent-dashboard',
      teamKey,
      'reports',
      'conversations-count',
      reportFilters.dateRange,
      reportFilters.agent,
      reportFilters.userType,
    ],
    enabled: canLoadDashboard && isConversationsReportSelected,
    staleTime: 60_000,
    queryFn: async () => {
      const response = await api.sessions.getConversationsCountReport({
        dateRange: reportFilters.dateRange,
        agent: reportFilters.agent,
        userType: reportFilters.userType,
      })

      if (!response.success) {
        throw new Error(
          response.error ||
            (response as { message?: string }).message ||
            'Failed to load conversations report'
        )
      }

      return response.data!
    },
  })

  useEffect(() => {
    if (conversationsCountQuery.error) {
      console.error(
        'Failed to load conversations count report:',
        conversationsCountQuery.error
      )
    }
  }, [conversationsCountQuery.error])

  const conversationsPerformanceQuery = useQuery<
    ConversationPerformanceReport,
    Error
  >({
    queryKey: [
      'agent-dashboard',
      teamKey,
      'reports',
      'conversations-performance',
      reportFilters.dateRange,
      reportFilters.agent,
      reportFilters.userType,
      reportFilters.country,
      reportFilters.language,
      reportFilters.browser,
      reportFilters.os,
    ],
    enabled: canLoadDashboard && isPerformanceReportSelected,
    staleTime: 60_000,
    queryFn: async () => {
      const response = await api.sessions.getConversationsPerformanceReport({
        dateRange: reportFilters.dateRange,
        agent: reportFilters.agent,
        userType: reportFilters.userType,
        country:
          reportFilters.country !== 'all' ? reportFilters.country : undefined,
        language:
          reportFilters.language !== 'all' ? reportFilters.language : undefined,
        browser:
          reportFilters.browser !== 'all' ? reportFilters.browser : undefined,
        os: reportFilters.os !== 'all' ? reportFilters.os : undefined,
      })

      if (!response.success) {
        throw new Error(
          response.error ||
            (response as { message?: string }).message ||
            'Failed to load conversations performance report'
        )
      }

      return response.data!
    },
  })

  useEffect(() => {
    if (conversationsPerformanceQuery.error) {
      console.error(
        'Failed to load conversations performance report:',
        conversationsPerformanceQuery.error
      )
    }
  }, [conversationsPerformanceQuery.error])

  const getSessionPriority = useCallback((session: AllActiveSession) => {
    const normalized =
      typeof session.status === 'string' ? session.status.toLowerCase() : ''

    if (normalized === 'active') {
      return 0
    }

    if (session.pendingHandoverRequest) {
      return 1
    }

    return 2
  }, [])

  const getSessionActivityTimestamp = useCallback(
    (session: AllActiveSession) => {
      const lastMessage = session.lastMessageAt
        ? new Date(session.lastMessageAt)
        : null
      const created = session.createdAt ? new Date(session.createdAt) : null
      const reference =
        lastMessage && !Number.isNaN(lastMessage.getTime())
          ? lastMessage
          : created && !Number.isNaN(created.getTime())
            ? created
            : null

      return reference ? reference.getTime() : 0
    },
    []
  )

  const stats: DashboardStats = statsQuery.data ?? defaultStats
  const handOverRequests: HandoverRequest[] = useMemo(
    () => (canManageHandovers ? (handoversQuery.data ?? []) : []),
    [canManageHandovers, handoversQuery.data]
  )

  // Filter pending handovers to only show those for agent's teams
  const pendingHandOverRequests: HandoverRequest[] = useMemo(() => {
    const userTeamIds = user?.teamIds ?? []
    return handOverRequests.filter(r => {
      // Only include pending requests
      if (typeof r.status !== 'string' || r.status.toUpperCase() !== 'PENDING')
        return false

      // If request has a teamId, check if agent belongs to that team
      if (r.teamId != null) {
        return userTeamIds.includes(r.teamId)
      }

      // Broadcast requests (no teamId) are visible to all
      return true
    })
  }, [handOverRequests, user])
  const activeSessions: ActiveSession[] = useMemo(
    () => (activeSessionsQuery.data as ActiveSession[] | undefined) ?? [],
    [activeSessionsQuery.data]
  )
  const allActiveSessions: AllActiveSession[] = useMemo(() => {
    const sessions: AllActiveSession[] =
      (allActiveSessionsQuery.data as AllActiveSession[] | undefined) ?? []
    return [...sessions].sort((sessionA, sessionB) => {
      const priorityDifference =
        getSessionPriority(sessionA) - getSessionPriority(sessionB)

      if (priorityDifference !== 0) {
        return priorityDifference
      }

      const timeDifference =
        getSessionActivityTimestamp(sessionB) -
        getSessionActivityTimestamp(sessionA)

      return timeDifference
    })
  }, [
    allActiveSessionsQuery.data,
    getSessionPriority,
    getSessionActivityTimestamp,
  ])
  const visitors = useMemo(() => visitorsQuery.data ?? [], [visitorsQuery.data])
  const supervisorSessionDetails = useMemo(
    () =>
      supervisorSessionId
        ? allActiveSessions.find(session => session.id === supervisorSessionId)
        : undefined,
    [allActiveSessions, supervisorSessionId]
  )
  const supervisorDefaultTeamId = useMemo(() => {
    const pendingTeamIds =
      supervisorSessionDetails?.pendingHandoverRequest?.teamIds ?? []
    const candidates: Array<number | string | null | undefined> = [
      supervisorSessionDetails?.lastAssignment?.teamId,
      supervisorSessionDetails?.pendingHandoverRequest?.teamId,
      supervisorSessionDetails?.teamId,
      Array.isArray(pendingTeamIds) && pendingTeamIds.length > 0
        ? pendingTeamIds[0]
        : null,
    ]
    for (const value of candidates) {
      if (value == null) {
        continue
      }
      const parsed = Number.parseInt(String(value), 10)
      if (Number.isFinite(parsed)) {
        return parsed
      }
    }
    return null
  }, [
    supervisorSessionDetails?.lastAssignment?.teamId,
    supervisorSessionDetails?.pendingHandoverRequest?.teamId,
    supervisorSessionDetails?.pendingHandoverRequest?.teamIds,
    supervisorSessionDetails?.teamId,
  ])

  const resolveTeamNameById = useCallback(
    (teamId: number | null | undefined): string | null => {
      if (teamId == null) {
        return null
      }
      const match = teamFilter.availableTeams.find(team => team.id === teamId)
      return match?.name ?? null
    },
    [teamFilter.availableTeams]
  )

  const applyDashboardAssignmentSnapshot = useCallback(
    (targetSessionId: string, teamId: number | null) => {
      const normalizedTeamId =
        teamId != null && Number.isFinite(teamId) ? teamId : null
      const teamName = resolveTeamNameById(normalizedTeamId)

      queryClient.setQueryData<ActiveSession[]>(
        ['agent-dashboard', teamKey, 'active-sessions'],
        current => {
          if (!Array.isArray(current)) {
            return current
          }
          let didChange = false
          const next = current.map(session => {
            if (session.id !== targetSessionId) {
              return session
            }
            if (session.agentName === null) {
              return session
            }
            didChange = true
            return {
              ...session,
              agentName: null,
            }
          })
          return didChange ? next : current
        }
      )

      queryClient.setQueryData<AllActiveSession[]>(
        ['agent-dashboard', teamKey, 'all-active-sessions'],
        current => {
          if (!Array.isArray(current)) {
            return current
          }
          let didChange = false
          const next = current.map(session => {
            if (session.id !== targetSessionId) {
              return session
            }
            didChange = true
            return {
              ...session,
              agentName: null,
              agentId: null,
              teamId: normalizedTeamId,
              teamName: teamName ?? session.teamName ?? null,
            }
          })
          return didChange ? next : current
        }
      )

      queryClient.setQueryData(
        ['agent-dashboard', teamKey, 'assigned-sessions'],
        (existing: any) => {
          if (!existing) {
            return existing
          }

          const patchSummary = (summary: any) => {
            if (!summary || summary.id !== targetSessionId) {
              return summary
            }
            let updated = false
            const nextSummary: any = { ...summary }
            if (nextSummary.assignedAgentName !== null) {
              nextSummary.assignedAgentName = null
              updated = true
            }
            if (nextSummary.assignedAgentId !== null) {
              nextSummary.assignedAgentId = null
              updated = true
            }
            if (nextSummary.isOwnedByCurrentAgent) {
              nextSummary.isOwnedByCurrentAgent = false
              updated = true
            }
            return updated ? nextSummary : summary
          }

          const applyToList = (list: any[]) => {
            let changed = false
            const nextList = list.map(item => {
              const patched = patchSummary(item)
              if (patched !== item) {
                changed = true
              }
              return patched
            })
            return changed ? nextList : list
          }

          if (Array.isArray(existing.sessions)) {
            const nextSessions = applyToList(existing.sessions)
            if (nextSessions === existing.sessions) {
              return existing
            }
            return { ...existing, sessions: nextSessions }
          }

          if (
            typeof existing === 'object' &&
            existing !== null &&
            Array.isArray(existing.pages)
          ) {
            let changed = false
            const nextPages = existing.pages.map((page: any) => {
              if (!page || !Array.isArray(page.sessions)) {
                return page
              }
              const nextSessions = applyToList(page.sessions)
              if (nextSessions !== page.sessions) {
                changed = true
                return { ...page, sessions: nextSessions }
              }
              return page
            })
            if (!changed) {
              return existing
            }
            return { ...existing, pages: nextPages }
          }

          return existing
        }
      )
    },
    [queryClient, resolveTeamNameById, teamKey]
  )

  useEffect(() => {
    if (visitorsQuery.error) {
      console.error('Failed to load visitors:', visitorsQuery.error)
    }
  }, [visitorsQuery.error])

  useEffect(() => {
    if (!isSupervisorModalOpen) {
      return
    }
    if (teamFilter.availableTeams.length === 0) {
      fetchTeams().catch(error => {
        console.error('Failed to fetch teams for supervisor actions:', error)
      })
    }
  }, [fetchTeams, isSupervisorModalOpen, teamFilter.availableTeams.length])

  const {
    data: supervisorAvailableAgents,
    isFetching: isFetchingSupervisorAgents,
    error: supervisorAgentsError,
  } = useQuery<SupervisorAvailableAgent[]>({
    queryKey: [
      'agent-dashboard',
      teamKey,
      'supervisor',
      'available-agents',
      supervisorSessionId,
    ],
    enabled:
      isSupervisor &&
      isSupervisorModalOpen &&
      supervisorSessionId !== null &&
      canLoadDashboard,
    staleTime: 5_000,
    queryFn: async () => {
      const response = await api.agent.listAgents()

      if (!response.success) {
        throw new Error(response.error || 'Failed to load available agents')
      }

      if (!Array.isArray(response.data)) {
        return []
      }

      return response.data.map(agent => {
        const fallbackCandidate =
          (agent as any)?.primaryTeamId ?? (agent as any)?.teamId ?? null
        const normalizedTeamIds = normalizeTeamIds(
          (agent as any)?.teamIds,
          fallbackCandidate
        )
        const primaryTeamId = derivePrimaryTeamId(
          normalizedTeamIds,
          fallbackCandidate
        )

        return {
          ...agent,
          teamIds: normalizedTeamIds,
          primaryTeamId,
          teamId: primaryTeamId ?? (agent as any)?.teamId ?? null,
          isOnline:
            typeof (agent as any)?.isOnline === 'boolean'
              ? Boolean((agent as any)?.isOnline)
              : null,
        } as SupervisorAvailableAgent
      })
    },
  })
  const supervisorAgentsErrorMessage = supervisorAgentsError
    ? supervisorAgentsError instanceof Error
      ? supervisorAgentsError.message
      : String(supervisorAgentsError)
    : null
  const availableAgentsForSupervisor = useMemo(() => {
    if (!supervisorAvailableAgents) {
      return []
    }

    const seenIds = new Set<number>()
    const seenUsernames = new Set<string>()

    const deduped = supervisorAvailableAgents.filter(agent => {
      const normalizedUsername =
        typeof agent?.username === 'string'
          ? agent.username.trim().toLowerCase()
          : null

      if (agent?.id != null) {
        const numericId = Number(agent.id)
        if (Number.isFinite(numericId)) {
          if (seenIds.has(numericId)) {
            return false
          }
          seenIds.add(numericId)
          if (normalizedUsername) {
            seenUsernames.add(normalizedUsername)
          }
          return true
        }
      }

      if (normalizedUsername) {
        if (seenUsernames.has(normalizedUsername)) {
          return false
        }
        seenUsernames.add(normalizedUsername)
        return true
      }

      return true
    })

    return deduped.sort((a, b) => {
      const onlineA = Number(Boolean(a.isOnline))
      const onlineB = Number(Boolean(b.isOnline))
      if (onlineA !== onlineB) {
        return onlineB - onlineA
      }
      const nameA = (
        a.firstName && a.lastName
          ? `${a.firstName} ${a.lastName}`
          : a.username || ''
      ).toLowerCase()
      const nameB = (
        b.firstName && b.lastName
          ? `${b.firstName} ${b.lastName}`
          : b.username || ''
      ).toLowerCase()
      return nameA.localeCompare(nameB)
    })
  }, [supervisorAvailableAgents])

  useEffect(() => {
    if (supervisorAgentsError) {
      console.error(
        'Failed to load available agents for supervisor actions:',
        supervisorAgentsError
      )
    }
  }, [supervisorAgentsError])

  const isInitialLoading =
    statsQuery.isLoading ||
    (handoverQueryEnabled ? handoversQuery.isLoading : false) ||
    activeSessionsQuery.isLoading ||
    allActiveSessionsQuery.isLoading ||
    visitorsQuery.isLoading

  const isFetchingDashboard =
    statsQuery.isFetching ||
    (handoverQueryEnabled ? handoversQuery.isFetching : false) ||
    activeSessionsQuery.isFetching ||
    allActiveSessionsQuery.isFetching ||
    visitorsQuery.isFetching

  const refreshDashboardData = useCallback(async () => {
    console.log('🔄 [DEBUG] Refreshing all dashboard data')

    // Invalidate all agent-dashboard queries with the current team filter
    await queryClient.invalidateQueries({
      queryKey: ['agent-dashboard', teamKey],
      exact: false,
    })

    // Also explicitly invalidate specific dashboard queries
    const queriesToInvalidate = [
      ['agent-dashboard', teamKey, 'stats'],
      ['agent-dashboard', teamKey, 'active-sessions'],
      ['agent-dashboard', teamKey, 'all-active-sessions'],
      ['agent-dashboard', teamKey, 'visitors'],
    ]

    if (canManageHandovers) {
      queriesToInvalidate.push(['agent-dashboard', teamKey, 'handovers'])
    }

    for (const queryKey of queriesToInvalidate) {
      await queryClient.invalidateQueries({
        queryKey,
        exact: true,
      })
    }

    console.log('🔄 [DEBUG] All dashboard data invalidated')
  }, [canManageHandovers, queryClient, teamKey])

  const releaseSessionToTeam = useCallback(
    async (
      targetSessionId: string,
      targetTeamId: number,
      reason?: string
    ): Promise<TakeoverResult> => {
      try {
        const response = await api.sessions.transferSessionToTeam(
          targetSessionId,
          targetTeamId,
          reason
        )

        if (!response.success) {
          const message =
            response.error || response.message || 'Failed to release session'
          notificationActions.showError('Team Assignment Failed', message)
          return { success: false, message }
        }

        const successMessage =
          response.message ||
          `Session ${String(targetSessionId).slice(-8)} returned to the team queue.`

        notificationActions.showSuccess('Session Released', successMessage)
        applyDashboardAssignmentSnapshot(targetSessionId, targetTeamId)
        await refreshDashboardData()

        return { success: true, message: successMessage }
      } catch (error) {
        console.error('Failed to release session to team:', error)
        const message =
          error instanceof Error
            ? error.message
            : 'Failed to release session to the selected team'
        notificationActions.showError('Team Assignment Failed', message)
        return { success: false, message }
      }
    },
    [
      applyDashboardAssignmentSnapshot,
      notificationActions,
      refreshDashboardData,
    ]
  )

  // Delegate to shared relative time formatter for consistent display
  const formatSessionTime = (dateString: string | Date | undefined | null) => {
    return formatRelativeTime(dateString ?? null)
  }

  const { addToast } = useToastNotificationStore()

  // Helper functions for toast notifications
  const showSuccess = useCallback(
    (title: string, message: string, actionUrl?: string) => {
      addToast({ type: 'success', title, message, urgent: false })
    },
    [addToast]
  )

  const showError = useCallback(
    (title: string, message: string) => {
      addToast({ type: 'error', title, message, urgent: false })
    },
    [addToast]
  )

  const showInfo = useCallback(
    (title: string, message: string, actionUrl?: string) => {
      addToast({ type: 'info', title, message, urgent: false })
    },
    [addToast]
  )

  const handleCopySessionId = useCallback(
    async (sessionId: string) => {
      try {
        if (
          typeof navigator !== 'undefined' &&
          navigator.clipboard &&
          typeof navigator.clipboard.writeText === 'function'
        ) {
          await navigator.clipboard.writeText(sessionId)
        } else if (typeof document !== 'undefined') {
          const tempTextArea = document.createElement('textarea')
          tempTextArea.value = sessionId
          tempTextArea.setAttribute('readonly', '')
          tempTextArea.style.position = 'absolute'
          tempTextArea.style.left = '-9999px'
          document.body.appendChild(tempTextArea)
          tempTextArea.select()
          document.execCommand('copy')
          document.body.removeChild(tempTextArea)
        }

        showSuccess(
          'Session ID copied',
          `Session ${String(sessionId).slice(-8)} copied to clipboard.`
        )
      } catch (error) {
        console.error('Failed to copy session ID:', error)
        showError(
          'Copy failed',
          'Unable to copy the session ID automatically. Please copy it manually.'
        )
      }
    },
    [showSuccess, showError]
  )

  // Filter panel visibility (independent of hook)
  const [showFilters, setShowFilters] = useState(false)
  const [sessionIdSearch, setSessionIdSearch] = useState('')
  const {
    filters,
    setFilters,
    allSessionsTeamFilter,
    handleAllSessionsTeamFilterChange,
    filteredSessions,
    activeFilterCount,
    resetFilters,
  } = useAllSessionsFiltering({
    sessions: allActiveSessions,
    user,
    isSupervisor,
  })

  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  // activeFilterCount provided by hook (legacy computation removed)

  const handleTokenExpiration = useCallback(() => {
    logout()
    router.push('/agent')
  }, [logout, router])

  useEffect(() => {
    let isCancelled = false

    const checkAuth = async () => {
      // Wait a brief moment to allow for store rehydration from localStorage
      await new Promise(resolve => setTimeout(resolve, 0))

      if (isCancelled) return

      if (authIsLoading) {
        return
      }

      if (!isAuthenticated) {
        if (!isCancelled) {
          router.replace('/agent')
        }
        return
      }

      // Only verify if we don't have user data but we're authenticated
      // This prevents unnecessary API calls that cause flicker
      if (!user && token) {
        const verifyAgent = async () => {
          try {
            const isValid = await verify()
            if (!isValid && !isCancelled) {
              router.replace('/agent')
            }
          } catch (error) {
            console.error('Agent verification failed:', error)
            if (!isCancelled) {
              router.replace('/agent')
            }
          }
        }

        verifyAgent()
      }

      // If we have user data, check roles immediately
      if (user && !isCancelled) {
        const roles = extractUserRoles(user)
        if (
          !roles.includes(UserRole.AGENT) &&
          !roles.includes(UserRole.SUPERVISOR)
        ) {
          router.replace('/agent')
        }
      }
    }

    checkAuth()

    return () => {
      isCancelled = true
    }
  }, [authIsLoading, isAuthenticated, router, user, verify, token])

  // WebSocket connection for real-time updates
  const wsUrl = process.env.NEXT_PUBLIC_WS_URL || 'ws://localhost:16001/ws'
  // Remove siteId query parameter logic
  // console.log('WebSocket URL:', wsUrl)
  // console.log('Environment NEXT_PUBLIC_WS_URL:', process.env.NEXT_PUBLIC_WS_URL)

  // Poll for visitors at regular intervals for real-time updates
  useEffect(() => {
    if (!canLoadDashboard) {
      return
    }

    const interval = setInterval(() => {
      queryClient.invalidateQueries({
        queryKey: ['agent-dashboard', teamKey, 'visitors'],
      })
    }, 5000)

    return () => clearInterval(interval)
  }, [canLoadDashboard, queryClient, teamKey])

  const {
    isConnected: wsConnected,
    disconnect: disconnectWebSocket,
    reconnect: reconnectWebSocket,
    registerAgent: registerAgentConnection,
  } = useWebSocket(wsUrl, {
    onConnect: () => {
      console.log('[Dashboard] 🔌 Agent dashboard WebSocket connected')
      console.log('[Dashboard] WebSocket URL:', wsUrl)
    },
    onDisconnect: () => {
      console.log('[Dashboard] 🔌 Agent dashboard WebSocket disconnected')
    },
    onMessage: message => {
      // console.log('[Dashboard] 📬 Raw WebSocket message received:', message)
    },
    onAgentStatusUpdate: data => {
      console.log('Agent status update:', data)
      if (data.agentId === user?.id) {
        setIsOnline(data.isOnline)
      }
    },
    onSessionUpdate: data => {
      console.log('Session update:', data)
      refreshDashboardData()

      if (data.status === 'active' && !data.isHandedOver && data.sessionId) {
        showInfo(
          'New Session',
          `Session ${String(data.sessionId).slice(-8)} is now active`,
          buildAgentDashboardChatUrl(data.sessionId)
        )
      }
    },
    onSessionAssigned: data => {
      if (!data.sessionId) {
        console.warn('Session assigned event received without sessionId:', data)
        return
      }

      if (data.agentId === user?.id) {
        showSuccess(
          'Session Assigned',
          `You have been assigned session ${String(data.sessionId).slice(-8)}`,
          buildAgentDashboardChatUrl(data.sessionId)
        )
        openChatSession(String(data.sessionId))
      } else {
        queryClient.setQueryData<HandoverRequest[] | undefined>(
          ['agent-dashboard', teamKey, 'handovers'],
          prev =>
            prev?.filter(request => request.sessionId !== data.sessionId) ?? []
        )
      }

      refreshDashboardData()
    },
    onHandoverRequest: data => {
      console.log('[Dashboard] 🔔 HANDOVER REQUEST RECEIVED!')
      console.log('[Dashboard] Handover data:', data)
      console.log('[Dashboard] Current user:', {
        id: user?.id,
        username: user?.username,
      })

      if (!data.sessionId) {
        console.warn('Handover request received without sessionId:', data)
        return
      }

      refreshDashboardData()

      const sessionShortId = String(data.sessionId).slice(-8)
      console.log(
        '[Dashboard] 🔔 HANDOVER REQUEST RECEIVED for session:',
        sessionShortId
      )
      console.log(
        '[Dashboard] Priority:',
        data.priority,
        'Reason:',
        data.reason
      )
    },
    onSessionTaken: data => {
      console.log('🔔 [DEBUG] Session taken WebSocket event received:', data)
      const { notifications, fetchNotifications, markSessionTaken } =
        useNotificationStore.getState()

      console.log(
        '🔍 [DEBUG] Current notifications before session taken update:',
        {
          total: notifications.length,
          notifications: notifications.map(n => ({
            id: n.id,
            sessionId: n.sessionId,
            isTaken:
              'isTaken' in n
                ? (n as Record<string, unknown>)['isTaken']
                : undefined,
          })),
        }
      )

      // Update local state immediately
      markSessionTaken(data.sessionId)

      console.log('🔍 [DEBUG] Notifications after markSessionTaken:', {
        total: notifications.length,
        notifications: notifications.map(n => ({
          id: n.id,
          sessionId: n.sessionId,
          isTaken:
            'isTaken' in n
              ? (n as Record<string, unknown>)['isTaken']
              : undefined,
        })),
      })

      // Also show toast notification
      notificationActions.showInfo(
        'Session Taken',
        `Session ${String(data.sessionId).slice(-8)} has been taken by another agent`
      )
    },
    onTransferRequestReceived: data => {
      console.log('[Dashboard] Transfer request received:', data)
      refreshDashboardData()
      showInfo(
        'Transfer Request Received',
        `Agent ${data.fromAgentName} wants to transfer session ${String(data.sessionId).slice(-8)} to you. Check your pending assignments.`
      )
    },
    onTransferRequestSent: data => {
      console.log('[Dashboard] Transfer request sent:', data)
      showSuccess(
        'Transfer Request Sent',
        'Transfer request sent successfully. Waiting for agent acceptance.'
      )
    },
    onNotification: data => {
      console.log('📬 [Dashboard] WebSocket notification received:', data)

      // 1. Refresh persistent notifications from database (FIRST)
      const { fetchNotifications, fetchUnreadCount, markSessionTaken } =
        useNotificationStore.getState()

      // Check if this is a session taken notification and update local state first
      // Cast data to any to access dynamic properties that may be included in the WebSocket message
      const notificationData = data as any
      if (
        notificationData.sessionId &&
        (notificationData.action === 'taken' ||
          notificationData.status === 'taken')
      ) {
        console.log(
          '🔄 [Dashboard] Session taken notification, updating local state first'
        )
        markSessionTaken(notificationData.sessionId)
      }

      // Fetch fresh notifications with a slight delay to avoid race conditions
      setTimeout(() => {
        fetchNotifications({ includeRead: false, includeDismissed: false })
        fetchUnreadCount()
        console.log(
          '✅ [Dashboard] Persistent notifications refreshed from database'
        )
      }, 100)
    },
    onVisitorConnected: data => {
      console.log('[Dashboard] 👤 Visitor connected event received:', data)
      queryClient.setQueryData<Visitor[] | undefined>(
        ['agent-dashboard', teamKey, 'visitors'],
        prev => {
          const current = prev ?? []
          const newVisitor: Visitor = {
            id: data.id,
            sessionId: data.sessionId,
            userId: data.userId,
            lastSeen: data.timestamp,
            isActive: true,
          }

          const exists = current.some(v => v.id === newVisitor.id)
          if (!exists) {
            console.log('[Dashboard] 👤 Adding new visitor to list:', data)
            return [...current, newVisitor]
          }

          console.log('[Dashboard] 👤 Visitor already exists in list:', data.id)
          return current.map(v =>
            v.id === newVisitor.id
              ? { ...v, isActive: true, lastSeen: newVisitor.lastSeen }
              : v
          )
        }
      )

      showInfo('New Visitor', 'A new visitor has connected to the site')
    },
    onVisitorDisconnected: data => {
      console.log('[Dashboard] 👤 Visitor disconnected event received:', data)
      queryClient.setQueryData<Visitor[] | undefined>(
        ['agent-dashboard', teamKey, 'visitors'],
        prev => prev?.filter(v => v.id !== data.id) ?? []
      )
    },
  })

  useEffect(() => {
    const authAndLoad = async () => {
      if (!isAuthenticated || authIsLoading) {
        return
      }

      if (user) {
        setIsOnline(user.isOnline ?? false)
      }

      await refreshDashboardData()
    }

    authAndLoad()
  }, [isAuthenticated, authIsLoading, user, refreshDashboardData])

  // Register WebSocket connection when user becomes available
  useEffect(() => {
    // console.log('[Dashboard] 🔍 Registration effect triggered:', {
    //   wsConnected,
    //   hasUser: !!user,
    //   userKeys: user ? Object.keys(user) : 'no user',
    //   userString: user ? JSON.stringify(user, null, 2) : 'no user',
    //   username: user?.username,
    //   role: agentRoleLabel,
    // })

    // Enhanced ID extraction with comprehensive validation
    let agentId = null
    let idSource = 'none'

    if (user) {
      // Check all possible ID properties with detailed logging
      if (user.id !== undefined && user.id !== null) {
        agentId = Number(user.id)
        idSource = 'user.id'
      }
      // Additional property inspection for debugging
      // console.log('[Dashboard] 🔍 User object property analysis:', {
      //   hasIdProp: user.hasOwnProperty('id'),
      //   idValue: user.id,
      //   idType: typeof user.id,
      //   extractedAgentId: agentId,
      //   idSource,
      // })
    }

    // Validate extracted ID
    const isValidAgentId = agentId && !isNaN(agentId) && agentId > 0

    // console.log('[Dashboard] 🔍 Registration conditions check:', {
    //   wsConnected,
    //   hasUser: !!user,
    //   hasValidAgentId: isValidAgentId,
    //   agentId,
    //   idSource,
    //   canRegister: wsConnected && user && isValidAgentId,
    // })

    if (wsConnected && user && isValidAgentId) {
      // console.log('[Dashboard] ✅ All conditions met for agent registration')
      console.log('[Dashboard] 📤 Registering agent with WebSocket:', {
        agentId,
        idSource,
        username: user.username,
        role: agentRoleLabel,
        email: user.email,
      })

      try {
        // Use the extracted and validated agentId for registration
        registerAgentConnection(agentId!)
        console.log(
          '[Dashboard] ✅ registerAgent() called successfully with ID:',
          agentId,
          'from:',
          idSource
        )

        // Send a test message to verify registration worked
        setTimeout(() => {
          console.log(
            '[Dashboard] ℹ️  Registration completed, connection status:',
            {
              wsConnected,
              isConnected: wsConnected,
            }
          )
        }, 1000)
      } catch (error) {
        console.error('[Dashboard] ❌ Error during agent registration:', error)
      }
    } else {
      console.log('[Dashboard] ⚠️  Registration conditions not met:', {
        wsConnected: wsConnected,
        hasUser: !!user,
        hasValidAgentId: isValidAgentId,
        agentId: agentId,
        idSource,
        userKeys: user ? Object.keys(user) : 'no user',
        userString: user ? JSON.stringify(user, null, 2) : 'no user',
      })

      // Enhanced debugging for user object structure issues
      if (user) {
        console.log('[Dashboard] 🕵️ Detailed user object inspection:')
        console.log(
          '  - hasIdProp:',
          user.hasOwnProperty('id'),
          '| value:',
          user.id,
          '| type:',
          typeof user.id
        )

        // Check if the issue is with the agent profile API response
        console.log(
          '[Dashboard] 💡 Suggestion: Check if /api/agent/profile returns user object with correct ID field'
        )
      }
    }
  }, [wsConnected, user, user?.id, agentRoleLabel, registerAgentConnection])

  // Re-register agent with sessionIds of assigned active sessions
  // This ensures direct message delivery for all sessions assigned to this agent
  useEffect(() => {
    if (
      !wsConnected ||
      !user?.id ||
      !activeSessions?.length ||
      !registerAgentConnection
    ) {
      return
    }

    const agentId = Number(user.id)
    if (!agentId || isNaN(agentId)) {
      return
    }

    // Register agent with each session ID for direct delivery
    activeSessions.forEach(session => {
      if (session.id) {
        // console.log('[Dashboard] 🔄 Registering agent with active session:', {
        //   agentId,
        //   sessionId: session.id,
        // })
        registerAgentConnection(agentId, session.id)
      }
    })
  }, [wsConnected, user?.id, activeSessions, registerAgentConnection])

  const handleLogout = async () => {
    await logout()
    router.push('/agent')
  }

  // Test function to manually add a visitor for debugging
  const testAddVisitor = () => {
    const testVisitor = {
      id: `test-${Date.now()}`,
      sessionId: `session-${Date.now()}`,
      userId: `user-${Date.now()}`,
      lastSeen: new Date().toISOString(),
    }
    queryClient.setQueryData<Visitor[] | undefined>(
      ['agent-dashboard', teamKey, 'visitors'],
      prev => [...(prev ?? []), { ...testVisitor, isActive: true }]
    )
    console.log('Added test visitor:', testVisitor)
  }

  // Team filter change handler
  const handleTeamFilterChange = useCallback(
    (teamId: number | null, showAll: boolean) => {
      const { setTeamFilter } = useTeamsStore.getState()
      setTeamFilter({
        selectedTeamId: teamId,
        showAllTeams: showAll,
      })
      // Reload dashboard data with new filter
      refreshDashboardData()
    },
    [refreshDashboardData]
  )
  const toggleOnlineStatus = useCallback(async () => {
    try {
      if (!token) {
        handleTokenExpiration()
        return
      }

      const newStatus = !isOnline
      await api.agent.updateStatus(newStatus)
      setIsOnline(newStatus)
    } catch (error) {
      console.error('Error updating status:', error)
    }
  }, [token, handleTokenExpiration, isOnline])

  const handleTakeOver = async (requestId: number) => {
    try {
      if (!token) {
        handleTokenExpiration()
        return
      }

      console.log('🔄 [DEBUG] Taking over handover request:', requestId)
      console.log('🔄 [DEBUG] Current handover requests:', handOverRequests)

      const response = await api.sessions.assignHandover(requestId)
      if (!response.success) {
        throw new Error(response.error || 'Failed to assign handover request')
      }

      console.log(
        '🔄 [DEBUG] Handover API status:',
        response.success ? 'success' : 'failed'
      )
      if (!response.success) {
        console.error('❌ [DEBUG] Handover API error:', response)
      }

      // Find the session ID for this request
      const request = handOverRequests.find(r => r.id === requestId)
      console.log('🔄 [DEBUG] Found handover request:', request)
      console.log('🔄 [DEBUG] Session ID from request:', request?.sessionId)

      // Update local state immediately for better UX
      queryClient.setQueryData(
        ['agent-dashboard', teamKey, 'handovers'],
        (old: HandoverRequest[] | undefined) =>
          old?.filter(req => req.id !== requestId) || []
      )

      // Refresh handover requests and active sessions
      console.log(
        '🔄 [DEBUG] Refreshing dashboard data after handover acceptance'
      )
      await refreshDashboardData()

      console.log('🔄 [DEBUG] Dashboard data refresh completed')

      // Send system message to user notifying agent has taken over
      if (request?.sessionId) {
        try {
          const systemMessage = {
            content: `An agent has joined the chat to help you. Hi, I'm ${user?.username || 'an agent'}!`,
            type: 'text',
            role: 'system', // Explicitly set as system message
          }

          // Use the agent message API with system role
          await api.agent.sendSessionMessage(request.sessionId, systemMessage)
          console.log('✅ [DEBUG] System message sent to user for handover:', {
            sessionId: request.sessionId,
            content: systemMessage.content,
            agentName: user?.username,
          })
        } catch (error) {
          console.error(
            '❌ [DEBUG] Failed to send system message to user:',
            error
          )
          // Don't fail the handover if the system message fails
        }
      }

      // Show success message and redirect to chat
      if (request) {
        console.log(
          '✅ [DEBUG] Successfully took over session:',
          request.sessionId
        )
        console.log('✅ [DEBUG] Session ID type:', typeof request.sessionId)
        console.log(
          '✅ [DEBUG] Will redirect to:',
          buildAgentDashboardChatUrl(request.sessionId ?? undefined)
        )

        // Show success notification using both persistent and toast
        notificationActions.showSuccess(
          'Session Taken Over',
          `You are now handling session ${String(request.sessionId ?? '').slice(-8) || ''}`
        )

        // Also show toast for immediate feedback
        notificationActions.showSuccess(
          'Session Accepted',
          `You have accepted session ${String(request.sessionId ?? '').slice(-8) || ''}`
        )

        // Try multiple approaches to get the session ID and redirect
        const sessionId = request.sessionId

        if (sessionId) {
          console.log('🚀 [DEBUG] Setting up redirect timeout...')
          setTimeout(() => {
            console.log('🚀 [DEBUG] Opening chat tab now...')
            openChatSession(String(sessionId))
          }, 300)
        } else {
          console.error(
            '❌ [DEBUG] No sessionId found in request, trying alternative approach'
          )

          // Alternative: Check if this session now appears in active sessions after refresh
          setTimeout(async () => {
            try {
              console.log(
                '🔄 [DEBUG] Trying to find newly assigned session in active sessions...'
              )
              const activeResponse = await api.sessions.getActiveSessions()
              if (activeResponse.success && activeResponse.data) {
                // Look for the most recently assigned session to this agent
                const newlyAssignedSession = activeResponse.data.find(
                  (s: any) =>
                    s.assignedTo === user?.id || s.agentId === user?.id
                )

                if (newlyAssignedSession) {
                  console.log(
                    '🎯 [DEBUG] Found newly assigned session:',
                    newlyAssignedSession.id
                  )
                  openChatSession(String(newlyAssignedSession.id))
                } else {
                  console.error(
                    '❌ [DEBUG] Could not find newly assigned session'
                  )
                }
              }
            } catch (error) {
              console.error(
                '❌ [DEBUG] Error finding newly assigned session:',
                error
              )
            }
          }, 1000)
        }
      } else {
        console.error(
          '❌ [DEBUG] Could not find handover request with ID:',
          requestId
        )
        console.error(
          '❌ [DEBUG] Available request IDs:',
          handOverRequests.map(r => r.id)
        )

        // Try alternative approach anyway
        console.log(
          '🔄 [DEBUG] Trying alternative approach without request data...'
        )
        setTimeout(async () => {
          try {
            const activeResponse = await api.sessions.getActiveSessions()
            if (activeResponse.success && activeResponse.data) {
              const newlyAssignedSession = activeResponse.data.find(
                (s: any) => s.assignedTo === user?.id || s.agentId === user?.id
              )

              if (newlyAssignedSession) {
                console.log(
                  '🎯 [DEBUG] Found newly assigned session via fallback:',
                  newlyAssignedSession.id
                )
                notificationActions.showSuccess(
                  'Session Taken Over',
                  `You are now handling session ${String(newlyAssignedSession.id).slice(-8)}`
                )
                openChatSession(String(newlyAssignedSession.id))
              }
            }
          } catch (error) {
            console.error('❌ [DEBUG] Error in fallback approach:', error)
          }
        }, 1000)
      }
    } catch (error) {
      console.error('Error taking over session:', error)

      if (error && typeof error === 'object' && 'status' in error) {
        console.error('Response status:', (error as any).status)
        console.error('Response data:', (error as any).data)

        // Handle token expiration
        if ((error as any).status === 401) {
          handleTokenExpiration()
          return
        }
      }
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'text-red-600 bg-red-100'
      case 'high':
        return 'text-orange-600 bg-orange-100'
      case 'normal':
        return 'text-blue-600 bg-blue-100'
      case 'low':
        return 'text-gray-600 bg-gray-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const getRequestTypeLabel = (
    type?: 'flow' | 'user_request' | 'agent_supervisor'
  ) => {
    switch (type) {
      case 'flow':
        return 'Flow Trigger'
      case 'agent_supervisor':
        return 'Agent / Supervisor'
      case 'user_request':
      default:
        return 'User Request'
    }
  }

  const getRequestTypeBadgeClass = (
    type?: 'flow' | 'user_request' | 'agent_supervisor'
  ) => {
    switch (type) {
      case 'flow':
        return 'bg-purple-100 text-purple-800'
      case 'agent_supervisor':
        return 'bg-blue-100 text-blue-800'
      case 'user_request':
      default:
        return 'bg-amber-100 text-amber-800'
    }
  }

  const getPlatformLabel = (platform: string) => {
    switch (platform) {
      case 'whatsapp':
        return 'WhatsApp'
      case 'facebook':
        return 'Facebook'
      case 'instagram':
        return 'Instagram'
      case 'telegram':
        return 'Telegram'
      case 'wechat':
        return 'WeChat'
      case 'line':
        return 'LINE'
      case 'web':
        return 'Web'
      default:
        return platform?.charAt(0).toUpperCase() + platform?.slice(1)
    }
  }

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'whatsapp':
        return <Phone className="h-4 w-4" />
      case 'facebook':
        return <Smartphone className="h-4 w-4" />
      default:
        return <Monitor className="h-4 w-4" />
    }
  }

  const handleTakeoverSession = useCallback(
    async (
      sessionId: string,
      takeoverOptions?: SessionTakeoverOptions
    ): Promise<TakeoverResult> => {
      const isRetriableTakeoverError = (error: unknown) => {
        if (error instanceof Error) {
          const message = error.message.toLowerCase()
          return (
            message.includes('network') ||
            message.includes('timeout') ||
            message.includes('failed to fetch')
          )
        }
        return false
      }

      try {
        if (!token) {
          handleTokenExpiration()
          return {
            success: false,
            message: 'Authentication required',
          }
        }

        if (takeoverInFlightRef.current.has(sessionId)) {
          notificationActions.showInfo(
            'Takeover In Progress',
            'Another takeover request is already processing for this session.'
          )
          return {
            success: false,
            message: 'Takeover already in progress',
          }
        }

        takeoverInFlightRef.current.add(sessionId)

        // Find the session to check teamId and assignment status
        const session = allActiveSessions.find(s => s.id === sessionId)

        const agentTeamIds = userTeamIdList

        let resolvedOptions: SessionTakeoverOptions | undefined =
          takeoverOptions ? { ...takeoverOptions } : undefined

        // Check if team selection is needed:
        // 1. Agent has multiple teams
        // 2. No teamId provided in options yet
        // 3. Session has no teamId OR session's teamId is not in agent's teams
        const sessionTeamId = session?.teamId
        const needsTeamSelection =
          agentTeamIds.length > 1 &&
          !resolvedOptions?.teamId &&
          (!sessionTeamId || !agentTeamIds.includes(sessionTeamId))

        if (needsTeamSelection) {
          openTakeoverTeamModalForSession({
            sessionId,
            options: resolvedOptions,
          })
          return {
            success: false,
            message: 'Team selection required',
          }
        }

        // If session has teamId that belongs to agent, preserve it
        if (
          sessionTeamId &&
          agentTeamIds.includes(sessionTeamId) &&
          !resolvedOptions?.teamId
        ) {
          resolvedOptions = {
            ...resolvedOptions,
            teamId: sessionTeamId,
          }
        }
        // Single team agents auto-select their team
        else if (agentTeamIds.length === 1 && !resolvedOptions?.teamId) {
          resolvedOptions = {
            ...resolvedOptions,
            teamId: agentTeamIds[0],
          }
        }

        const performTakeover = async () => {
          let lastError: unknown = null
          for (let attempt = 1; attempt <= 2; attempt += 1) {
            try {
              const response = await api.sessions.takeoverSession(
                sessionId,
                resolvedOptions
              )
              if (!response.success) {
                throw new Error(
                  response.error || 'Failed to update session assignment'
                )
              }
              return response
            } catch (error) {
              lastError = error
              if (!isRetriableTakeoverError(error) || attempt === 2) {
                throw error
              }
              await new Promise(resolve => setTimeout(resolve, 300 * attempt))
            }
          }
          throw lastError instanceof Error
            ? lastError
            : new Error('Failed to take over the session')
        }

        const response = await performTakeover()

        const responseRecord: Record<string, unknown> = isRecord(response.data)
          ? (response.data as Record<string, unknown>)
          : {}

        const assignmentCandidate = responseRecord['assignment']
        const assignmentRecord = isRecord(assignmentCandidate)
          ? (assignmentCandidate as Record<string, unknown>)
          : null

        const actionCandidate = (() => {
          const rawAction = responseRecord['action']
          if (typeof rawAction === 'string') {
            return rawAction
          }
          return resolvedOptions?.action
        })()

        const serverAction: TakeoverActionType =
          actionCandidate === 'takeover' ||
          actionCandidate === 'force_takeover' ||
          actionCandidate === 'assign_agent' ||
          actionCandidate === 'assign_team'
            ? actionCandidate
            : 'takeover'

        const assignmentAgentIdValue = assignmentRecord
          ? assignmentRecord['agentId']
          : undefined
        const parsedAssignmentAgentId =
          typeof assignmentAgentIdValue === 'number'
            ? assignmentAgentIdValue
            : typeof assignmentAgentIdValue === 'string'
              ? Number.parseInt(assignmentAgentIdValue, 10)
              : NaN
        const assignmentAgentId = Number.isFinite(parsedAssignmentAgentId)
          ? parsedAssignmentAgentId
          : null

        const assignmentAgentNameValue = assignmentRecord
          ? assignmentRecord['agentName']
          : undefined
        const assignmentAgentName =
          typeof assignmentAgentNameValue === 'string'
            ? assignmentAgentNameValue
            : null
        const isAssignedToCurrentAgent =
          assignmentAgentId != null &&
          resolvedAgentId != null &&
          assignmentAgentId === resolvedAgentId

        const shouldSendSystemMessage =
          isAssignedToCurrentAgent &&
          (serverAction === 'takeover' || serverAction === 'force_takeover')

        const fallbackMessages: Record<TakeoverActionType, string> = {
          takeover: `You are now handling session ${String(sessionId).slice(-8)}`,
          force_takeover: `You are now handling session ${String(sessionId).slice(-8)}`,
          assign_agent: `Session ${String(sessionId).slice(-8)} assigned to ${assignmentAgentName || 'the selected agent'}.`,
          assign_team: `Session ${String(sessionId).slice(-8)} returned to the team queue.`,
        }

        const messageField = responseRecord['message']
        const successMessage =
          typeof messageField === 'string' && messageField.length
            ? messageField
            : fallbackMessages[serverAction]

        await refreshDashboardData()

        if (shouldSendSystemMessage) {
          try {
            const systemMessage = {
              content: `An agent has joined the chat to help you. Hi, I'm ${user?.username || 'an agent'}!`,
              type: 'text',
              role: 'system',
            }

            await api.agent.sendSessionMessage(sessionId, systemMessage)
            console.log(
              '✅ [DEBUG] System message sent to user for session takeover:',
              {
                sessionId,
                content: systemMessage.content,
                agentName: user?.username,
              }
            )
          } catch (error) {
            console.error(
              '❌ [DEBUG] Failed to send system message to user:',
              error
            )
            // Don't fail the takeover if the system message fails
          }
        }

        if (serverAction === 'assign_agent' && !isAssignedToCurrentAgent) {
          notificationActions.showSuccess('Session Reassigned', successMessage)
        } else if (serverAction === 'assign_team') {
          notificationActions.showSuccess('Session Released', successMessage)
        } else {
          notificationActions.showSuccess('Session Taken Over', successMessage)
        }

        if (
          shouldSendSystemMessage ||
          (isAssignedToCurrentAgent &&
            (serverAction === 'assign_agent' ||
              serverAction === 'force_takeover'))
        ) {
          setTimeout(() => {
            openChatSession(String(sessionId))
          }, 300)
        }

        return {
          success: true,
          message: successMessage,
        }
      } catch (error) {
        console.error('Error taking over session:', error)

        const extractedMessage =
          (error &&
          typeof error === 'object' &&
          'data' in error &&
          typeof (error as any).data?.error === 'string' &&
          (error as any).data.error.trim().length > 0
            ? (error as any).data.error.trim()
            : null) ??
          (error instanceof Error && error.message ? error.message : null)

        const messageText =
          extractedMessage && extractedMessage.length > 0
            ? extractedMessage
            : 'Failed to take over the session'

        if (messageText.toLowerCase().includes('authentication required')) {
          handleTokenExpiration()
          return {
            success: false,
            message: messageText,
          }
        }

        if (messageText.toLowerCase().includes('already assigned to you')) {
          notificationActions.showInfo(
            'Session Already Assigned',
            'Opening chat for the existing assignment.'
          )
          setTimeout(() => {
            openChatSession(String(sessionId))
          }, 300)

          return {
            success: true,
            message: messageText,
          }
        }

        const assignedTo =
          error &&
          typeof error === 'object' &&
          'data' in error &&
          typeof (error as any).data?.assignedTo === 'string'
            ? (error as any).data.assignedTo
            : undefined

        if (messageText.toLowerCase().includes('already assigned')) {
          const warningCopy =
            assignedTo &&
            assignedTo.trim().length > 0 &&
            !messageText.includes(assignedTo)
              ? `${messageText} This session is currently assigned to ${assignedTo.trim()}.`
              : messageText

          notificationActions.showWarning(
            'Session Already Assigned',
            warningCopy
          )
          await refreshDashboardData()

          return {
            success: false,
            message: warningCopy,
          }
        }

        notificationActions.showError('Takeover Failed', messageText)
        return {
          success: false,
          message: messageText,
        }
      } finally {
        takeoverInFlightRef.current.delete(sessionId)
      }
    },
    [
      token,
      handleTokenExpiration,
      takeoverInFlightRef,
      refreshDashboardData,
      notificationActions,
      openChatSession,
      resolvedAgentId,
      user?.username,
      allActiveSessions,
      userTeamIdList,
      openTakeoverTeamModalForSession,
    ]
  )

  const handleConfirmTakeoverTeamSelection = useCallback(async () => {
    if (!pendingTakeoverSessionId || selectedTakeoverTeamId == null) {
      return
    }

    setIsTakeoverTeamSubmitting(true)

    try {
      // Check if this is an accept action
      if (pendingAcceptRequestId) {
        const response = await api.agent.respondToHandoverAssignment(
          pendingAcceptRequestId,
          {
            response: 'accepted',
            reason: '',
            teamId: selectedTakeoverTeamId,
          }
        )

        if (!response.success) {
          throw new Error(response.error || 'Failed to accept handover')
        }

        notificationActions.showSuccess(
          'Handover Accepted',
          `You are now handling session ${String(pendingTakeoverSessionId).slice(-8)}`
        )

        await refreshDashboardData()

        setTimeout(() => {
          openChatSession(String(pendingTakeoverSessionId))
        }, 300)
        closeTakeoverTeamModal()
      } else {
        // Regular takeover action
        const updatedOptions: SessionTakeoverOptions = {
          ...pendingTakeoverOptions,
          teamId: selectedTakeoverTeamId,
        }

        const result = await handleTakeoverSession(
          pendingTakeoverSessionId,
          updatedOptions
        )
        if (result.success) {
          closeTakeoverTeamModal()
        }
      }
    } catch (error) {
      if (pendingAcceptRequestId) {
        console.error('Error accepting handover:', error)
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to accept handover'
        notificationActions.showError('Accept Failed', errorMessage)
      }
    } finally {
      setIsTakeoverTeamSubmitting(false)
    }
  }, [
    pendingTakeoverSessionId,
    selectedTakeoverTeamId,
    pendingTakeoverOptions,
    pendingAcceptRequestId,
    handleTakeoverSession,
    notificationActions,
    refreshDashboardData,
    openChatSession,
    closeTakeoverTeamModal,
    setIsTakeoverTeamSubmitting,
  ])

  const handleAcceptHandover = useCallback(
    async (session: AllActiveSession) => {
      try {
        if (!token) {
          handleTokenExpiration()
          return
        }

        const handoverRequest = session.pendingHandoverRequest
        if (!handoverRequest?.id) {
          notificationActions.showError(
            'Accept Failed',
            'No pending handover request found'
          )
          return
        }

        // Check if team selection is needed (broadcast handover with multiple teams)
        const isBroadcastHandover = !handoverRequest.teamId
        const agentTeamIds = userTeamIdList

        if (isBroadcastHandover && agentTeamIds.length > 1) {
          // Show team selection modal for accept action
          openTakeoverTeamModalForAccept(session.id, handoverRequest.id)
          return
        }

        // Use existing teamId from handover request, or fallback to session teamId, or agent's first team
        const teamIdToUse =
          handoverRequest.teamId || session.teamId || agentTeamIds[0] || null

        const response = await api.agent.respondToHandoverAssignment(
          handoverRequest.id,
          {
            response: 'accepted',
            reason: '',
            teamId: teamIdToUse || undefined,
          }
        )

        if (!response.success) {
          throw new Error(response.error || 'Failed to accept handover')
        }

        notificationActions.showSuccess(
          'Handover Accepted',
          `You are now handling session ${String(session.id).slice(-8)}`
        )

        await refreshDashboardData()

        // Open chat session
        setTimeout(() => {
          openChatSession(String(session.id))
        }, 300)
      } catch (error) {
        console.error('Error accepting handover:', error)
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to accept handover'
        notificationActions.showError('Accept Failed', errorMessage)
      }
    },
    [
      token,
      handleTokenExpiration,
      notificationActions,
      refreshDashboardData,
      openChatSession,
      userTeamIdList,
      openTakeoverTeamModalForAccept,
    ]
  )

  const [supervisorActionError, setSupervisorActionError] = useState<
    string | null
  >(null)

  const openSupervisorManagement = useCallback((sessionId: string) => {
    setSupervisorActionError(null)
    setSupervisorSessionId(sessionId)
    setIsSupervisorModalOpen(true)
  }, [])

  const handleSupervisorSubmit = useCallback(
    async ({ teamId, agentId, notes }: SupervisorManageDialogSubmit) => {
      if (!supervisorSessionId) {
        return
      }

      setSupervisorActionError(null)
      setIsSupervisorActionLoading(true)
      try {
        const reason =
          notes && notes.trim().length > 0 ? notes.trim() : undefined

        const isAlreadyAssignedMessage = (message?: string | null) =>
          typeof message === 'string' &&
          message.toLowerCase().includes('already assigned')

        const assignAgent = () =>
          handleTakeoverSession(supervisorSessionId, {
            action: 'assign_agent',
            targetAgentId: agentId as number,
            reason,
            teamId,
          })

        const releaseToTeam = () => {
          if (teamId == null) {
            return Promise.resolve({
              success: false,
              message: 'Select a team before assigning.',
            })
          }
          return releaseSessionToTeam(supervisorSessionId, teamId, reason)
        }

        let takeoverResult: TakeoverResult

        if (agentId != null) {
          takeoverResult = await assignAgent()

          if (
            !takeoverResult.success &&
            isAlreadyAssignedMessage(takeoverResult.message)
          ) {
            const releaseResult = await releaseToTeam()

            if (!releaseResult.success) {
              const fallback = 'Unable to update session assignment.'
              setSupervisorActionError(
                releaseResult.message && releaseResult.message.trim().length > 0
                  ? releaseResult.message
                  : fallback
              )
              return
            }

            const normalizedTeamId =
              teamId != null && Number.isFinite(teamId) ? teamId : null
            applyDashboardAssignmentSnapshot(
              supervisorSessionId,
              normalizedTeamId
            )

            takeoverResult = await assignAgent()
          }
        } else {
          const releaseResult = await releaseToTeam()
          if (!releaseResult.success) {
            const fallback = 'Unable to update session assignment.'
            setSupervisorActionError(
              releaseResult.message && releaseResult.message.trim().length > 0
                ? releaseResult.message
                : fallback
            )
            return
          }

          takeoverResult = releaseResult
        }

        if (takeoverResult.success) {
          const normalizedTeamId =
            teamId != null && Number.isFinite(teamId) ? teamId : null
          applyDashboardAssignmentSnapshot(
            supervisorSessionId,
            normalizedTeamId
          )
          setIsSupervisorModalOpen(false)
          setSupervisorSessionId(null)
          setSupervisorActionError(null)
        } else {
          const fallback = 'Unable to update session assignment.'
          setSupervisorActionError(
            takeoverResult.message && takeoverResult.message.trim().length > 0
              ? takeoverResult.message
              : fallback
          )
        }
      } finally {
        setIsSupervisorActionLoading(false)
      }
    },
    [
      applyDashboardAssignmentSnapshot,
      handleTakeoverSession,
      releaseSessionToTeam,
      supervisorSessionId,
    ]
  )

  // Function to start a chat with a visitor
  const handleStartChatWithVisitor = async (visitorId: string) => {
    try {
      if (!token) {
        handleTokenExpiration()
        return
      }

      console.log('🔄 Starting chat with visitor:', visitorId)

      // Call API to initiate chat with visitor
      const response = await api.sessions.initiateVisitorChat(visitorId)

      const { sessionId } = response?.data || {}
      console.log('✅ Chat initiated successfully with session:', sessionId)

      if (sessionId) {
        // Show success notification
        notificationActions.showSuccess(
          'Chat Initiated',
          `Chat session created with visitor`
        )

        // Open the chat tab for the new session
        openChatSession(String(sessionId))
        queryClient.invalidateQueries({
          queryKey: ['agent-dashboard', teamKey, 'assigned-sessions'],
        })
      } else {
        notificationActions.showWarning(
          'Chat Initiation Failed',
          'Could not determine the new session ID.'
        )
      }
    } catch (error) {
      console.error('Error starting chat with visitor:', error)

      // Handle token expiration
      if (
        error &&
        typeof error === 'object' &&
        'status' in error &&
        error.status === 401
      ) {
        handleTokenExpiration()
        return
      }

      // Show error notification
      const errorMessage =
        error && typeof error === 'object' && 'message' in error
          ? (error.message as string) || 'Failed to start chat with visitor'
          : 'Failed to start chat with visitor'

      notificationActions.showError('Chat Initiation Failed', errorMessage)
    }
  }

  // (Filtering logic moved to useAllSessionsFiltering hook)

  // Reset pagination when filtered set changes
  useEffect(() => {
    setCurrentPage(1)
  }, [filteredSessions, sessionIdSearch])

  // Hook provides resetFilters; wrap to also reset page
  const handleResetAllSessionsFilters = () => {
    resetFilters()
    setSessionIdSearch('')
    setCurrentPage(1)
  }

  // Apply session ID search on top of filtered sessions
  const sessionsAfterSearch = useMemo(() => {
    if (!sessionIdSearch.trim()) {
      return filteredSessions
    }
    const searchLower = sessionIdSearch.toLowerCase().trim()
    return filteredSessions.filter(session =>
      session.id.toLowerCase().includes(searchLower)
    )
  }, [filteredSessions, sessionIdSearch])

  // Calculate pagination for filtered sessions
  const matchingSessionsCount = sessionsAfterSearch.length
  const totalPages = Math.ceil(matchingSessionsCount / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentSessions = sessionsAfterSearch.slice(startIndex, endIndex)
  const visibleRangeStart = matchingSessionsCount === 0 ? 0 : startIndex + 1
  const visibleRangeEnd =
    matchingSessionsCount === 0 ? 0 : Math.min(endIndex, matchingSessionsCount)

  useEffect(() => {
    if (totalPages === 0) {
      if (currentPage !== 1) {
        setCurrentPage(1)
      }
      return
    }

    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  // Show loading overlay but keep dashboard structure stable for e2e tests
  const showLoadingOverlay = isInitialLoading && !isHydrated

  return (
    <div
      className="min-h-screen bg-gray-50 relative"
      data-testid="agent-dashboard-ready"
    >
      {/* Loading overlay - shown during initial load but doesn't remove the underlying structure */}
      {showLoadingOverlay && (
        <div className="absolute inset-0 bg-gray-50/90 flex items-center justify-center z-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
            <p className="mt-4 text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      )}
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-blue-600 mr-3" />
              <h1 className="text-2xl font-bold text-gray-900">
                Agent Dashboard
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              {/* WebSocket Status */}
              <div className="flex items-center space-x-2">
                <div className="flex items-center space-x-1">
                  {wsConnected ? (
                    <Wifi className="h-4 w-4 text-green-500" />
                  ) : (
                    <WifiOff className="h-4 w-4 text-red-500" />
                  )}
                  <span className="text-xs text-gray-500">
                    {wsConnected ? 'Connected' : 'Disconnected'}
                  </span>
                  {!wsConnected && (
                    <button
                      onClick={() => {
                        console.log('Manual WebSocket reconnect attempt')
                        disconnectWebSocket(1000, 'Manual reconnect')
                        setTimeout(() => {
                          reconnectWebSocket()
                          if (user?.id) {
                            registerAgentConnection(Number(user.id))
                          }
                        }, 1000)
                      }}
                      className="text-xs text-blue-500 hover:text-blue-700 ml-2"
                    >
                      Retry
                    </button>
                  )}
                </div>
              </div>

              {/* Refresh Button */}
              <button
                type="button"
                onClick={refreshDashboardData}
                disabled={isFetchingDashboard}
                className="flex items-center px-2 py-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md disabled:opacity-50"
                title="Refresh data"
              >
                <RefreshCw
                  className={`h-4 w-4 ${isFetchingDashboard ? 'animate-spin' : ''}`}
                />
              </button>

              {/* WebSocket Status */}
              <div
                className={`px-2 py-1 text-xs rounded ${wsConnected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
              >
                WS: {wsConnected ? 'Connected' : 'Disconnected'}
              </div>

              {/* Notification Center */}
              <NotificationCenter />

              {/* Agent Status */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Status:</span>
                <button
                  type="button"
                  data-testid="agent-status-toggle"
                  onClick={toggleOnlineStatus}
                  className={`flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    isOnline
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  <div
                    className={`w-2 h-2 rounded-full mr-2 ${
                      isOnline ? 'bg-green-500' : 'bg-gray-500'
                    }`}
                  />
                  {isOnline ? 'Online' : 'Offline'}
                </button>
              </div>

              {/* Agent Teams */}
              {agentTeamNames.length > 0 && (
                <div className="hidden md:block relative group">
                  <div className="flex items-center space-x-2 px-3 py-1 rounded-lg hover:bg-gray-100 transition-colors cursor-default">
                    <Users className="h-4 w-4 text-gray-400" />
                    <span className="text-xs text-gray-500">
                      {agentTeamNames.length}{' '}
                      {agentTeamNames.length === 1 ? 'team' : 'teams'}
                    </span>
                  </div>
                  {/* Tooltip on hover */}
                  <div className="absolute right-0 top-full mt-2 w-auto min-w-[200px] max-w-xs opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3">
                      <div className="flex flex-wrap gap-1">
                        {agentTeamNames.map(teamName => (
                          <span
                            key={teamName}
                            className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700 border border-blue-100"
                          >
                            {teamName}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* User Info */}
              <div className="flex items-center space-x-2">
                <User className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-600">{user?.username}</span>
                <span className="text-xs text-gray-400">
                  ({agentRoleLabel})
                </span>
              </div>

              {/* Logout */}
              <button
                type="button"
                data-testid="agent-logout-button"
                onClick={handleLogout}
                className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
              >
                <LogOut className="h-4 w-4 mr-1" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-6 lg:flex-row">
          <div className="lg:hidden">
            {isHydrated && (
              <Sheet
                open={isMobileSidebarOpen}
                onOpenChange={setIsMobileSidebarOpen}
              >
                <SheetTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="flex w-full items-center justify-between gap-3"
                  >
                    <span className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">
                        Assigned Sessions
                      </span>
                    </span>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-full max-w-md p-0">
                  <SheetTitle className="sr-only">Assigned Sessions</SheetTitle>
                  <div className="flex h-full flex-col">
                    <div className="border-b px-4 py-3">
                      <h2 className="text-base font-semibold text-gray-900">
                        Assigned Sessions
                      </h2>
                      <p className="text-sm text-gray-500">
                        Review and manage your active chats.
                      </p>
                    </div>
                    <div className="flex-1 overflow-y-auto px-4 py-4">
                      <AssignedSessionsSidebar
                        currentAgentId={resolvedAgentId}
                        includeTeamSessions
                        teamKey={teamKey}
                        onSessionSelect={handleMobileSessionSelect}
                      />
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            )}
          </div>

          <div
            ref={sidebarRef}
            className={`relative hidden w-full lg:block lg:w-auto lg:flex-shrink-0 ${
              isResizingSidebar ? 'lg:select-none' : ''
            }`}
            style={computedSidebarStyle}
          >
            <AssignedSessionsSidebar
              currentAgentId={resolvedAgentId}
              includeTeamSessions
              teamKey={teamKey}
              onSessionSelect={openChatSession}
              onCollapse={() => setIsMainSidebarCollapsed(true)}
            />
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize assigned sessions sidebar"
              onPointerDown={handleSidebarPointerDown}
              className={`absolute right-0 top-0 hidden h-full w-1.5 cursor-col-resize transition-colors lg:block ${
                isResizingSidebar
                  ? 'bg-blue-200'
                  : 'bg-transparent hover:bg-gray-200'
              }`}
            />
          </div>

          <div className="flex-1 relative">
            {/* Expand sidebar button - floating */}
            {isMainSidebarCollapsed && (
              <div className="absolute top-[8px] left-0 z-20 hidden lg:block">
                <button
                  type="button"
                  onClick={() => setIsMainSidebarCollapsed(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-md border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                  title="Expand sidebar"
                >
                  <PanelLeftOpen className="h-4 w-4" />
                </button>
              </div>
            )}

            <div
              className={`mb-8 pl-0 ${isMainSidebarCollapsed ? 'lg:pl-8' : 'lg:pl-0'}`}
            >
              {/* Tabs section - render immediately for e2e test stability */}
              <ErrorBoundary>
                <Tabs
                  orientation="horizontal"
                  value={activeSessionsTab}
                  onValueChange={handleTabChange}
                  suppressHydrationWarning
                >
                  <TabsList
                    className={`relative flex w-full overflow-x-auto scrollbar-hide border-b border-gray-200 bg-white ${
                      isMainSidebarCollapsed ? 'lg:ml-10' : ''
                    }`}
                    data-testid="dashboard-tabs"
                  >
                    <TabsTrigger
                      value="overview"
                      data-testid="tab-overview"
                      className="whitespace-nowrap"
                    >
                      <span className="hidden sm:inline">Overview</span>
                      <span className="sm:hidden">📊</span>
                      {(pendingHandOverRequests.length > 0 ||
                        visitors.length > 0) && (
                        <span className="ml-1 sm:ml-2 inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {pendingHandOverRequests.length + visitors.length}
                        </span>
                      )}
                    </TabsTrigger>
                    <TabsTrigger
                      value="chat"
                      data-testid="tab-chat"
                      className="whitespace-nowrap"
                    >
                      <span className="hidden sm:inline">Chat</span>
                      <span className="sm:hidden">💬</span>
                    </TabsTrigger>
                    <TabsTrigger
                      value="my"
                      data-testid="tab-my"
                      className="whitespace-nowrap"
                    >
                      <span className="hidden sm:inline">My</span>
                      <span className="sm:hidden">👤</span>
                      {activeSessions?.length > 0 && (
                        <span className="ml-1 sm:ml-2 inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {activeSessions.length}
                        </span>
                      )}
                    </TabsTrigger>
                    <TabsTrigger
                      value="all"
                      data-testid="tab-all"
                      className="whitespace-nowrap"
                    >
                      <span className="hidden sm:inline">Requests</span>
                      <span className="sm:hidden">📋</span>
                      {allActiveSessions.length > 0 && (
                        <span className="ml-1 sm:ml-2 inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {allActiveSessions.length}
                        </span>
                      )}
                    </TabsTrigger>
                    <TabsTrigger
                      value="articles"
                      data-testid="tab-articles"
                      className="whitespace-nowrap"
                    >
                      <span className="hidden sm:inline">Articles</span>
                      <span className="sm:hidden">📄</span>
                    </TabsTrigger>
                    <TabsTrigger
                      value="tickets"
                      data-testid="tab-tickets"
                      className="whitespace-nowrap"
                    >
                      <span className="hidden sm:inline">Tickets</span>
                      <span className="sm:hidden">🎫</span>
                    </TabsTrigger>
                    <TabsTrigger
                      value="reports"
                      data-testid="tab-reports"
                      className="whitespace-nowrap"
                    >
                      <span className="hidden sm:inline">Reports</span>
                      <span className="sm:hidden">📊</span>
                    </TabsTrigger>
                    <button
                      type="button"
                      className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 text-muted-foreground hover:bg-gray-100"
                      onClick={() => router.push('/agent/call-center')}
                    >
                      <Phone className="mr-1 h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Call Center</span>
                      <span className="sm:hidden">📞</span>
                    </button>
                    {/* temp disabled for now */}
                    {false && (canManageTeamSetup || isSupervisor) && (
                      <TabsTrigger value="config" className="whitespace-nowrap">
                        <span className="hidden sm:inline">Config</span>
                        <span className="sm:hidden">⚙️</span>
                      </TabsTrigger>
                    )}
                  </TabsList>
                  {/* Handover Requests Queue */}
                  {/* Visitor Access */}
                  <TabsContent
                    value="overview"
                    className="bg-white shadow rounded-lg py-2"
                  >
                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                      <div className="bg-white overflow-hidden shadow rounded-lg h-28">
                        <div className="p-5 h-full flex items-center">
                          <div className="w-full flex items-center gap-4">
                            <div className="flex-shrink-0">
                              <MessageSquare className="h-6 w-6 text-blue-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1 flex flex-col justify-center">
                              <dl>
                                <dt className="text-sm font-medium text-gray-500 truncate">
                                  Active Sessions
                                </dt>
                                <dd className="text-lg font-medium text-gray-900">
                                  {stats.activeSessions}
                                </dd>
                              </dl>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white overflow-hidden shadow rounded-lg h-28">
                        <div className="p-5 h-full flex items-center">
                          <div className="w-full flex items-center">
                            <div className="shrink-0">
                              <AlertTriangle className="h-6 w-6 text-orange-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                              <dl>
                                <dt className="text-sm font-medium text-gray-500 truncate">
                                  Pending Request(s)
                                </dt>
                                <dd className="text-lg font-medium text-gray-900">
                                  {stats.pendingHandovers}
                                </dd>
                              </dl>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white overflow-hidden shadow rounded-lg h-28">
                        <div className="p-5 h-full flex items-center">
                          <div className="w-full flex items-center">
                            <div className="shrink-0">
                              <CheckCircle className="h-6 w-6 text-green-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                              <dl>
                                <dt className="text-sm font-medium text-gray-500 truncate">
                                  Completed Today
                                </dt>
                                <dd className="text-lg font-medium text-gray-900">
                                  {stats.completedToday}
                                </dd>
                              </dl>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white overflow-hidden shadow rounded-lg h-28">
                        <div className="p-5 h-full flex items-center">
                          <div className="w-full flex items-center">
                            <div className="shrink-0">
                              <Users className="h-6 w-6 text-purple-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                              <dl>
                                <dt className="text-sm font-medium text-gray-500 truncate">
                                  Total Sessions
                                </dt>
                                <dd className="text-lg font-medium text-gray-900">
                                  {stats.totalSessions}
                                </dd>
                              </dl>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-2 mx-4 flex justify-end">
                      <TeamFilter onFilterChange={handleTeamFilterChange} />
                    </div>

                    <div className="px-4 py-5 sm:p-6">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                        Pending Handover Requests (team/unassigned)
                      </h3>

                      {canManageHandovers ? (
                        <>
                          <div className="my-2">
                            <HandoverNotifications
                              onUpdate={refreshDashboardData}
                              showHistory={false}
                              mode="pendingRequests"
                              teamId={selectedTeamIdParam ?? null}
                            />
                          </div>
                          {pendingHandOverRequests.length === 0 && (
                            <div className="text-center py-8">
                              <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
                              <h3 className="mt-2 text-sm font-medium text-gray-900">
                                No pending requests
                              </h3>
                              <p className="mt-1 text-sm text-gray-500">
                                All chat sessions are currently handled by the
                                bot.
                              </p>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-center py-8">
                          <Shield className="mx-auto h-12 w-12 text-gray-400" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900">
                            Permission required
                          </h3>
                          <p className="mt-1 text-sm text-gray-500">
                            You need the CHAT_TRANSFER permission to view and
                            respond to handover requests.
                          </p>
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent
                    value="articles"
                    className="bg-white shadow rounded-lg"
                  >
                    <div className="p-6">
                      <AgentArticlesPanel />
                    </div>
                  </TabsContent>

                  <TabsContent
                    value="tickets"
                    className="bg-white shadow rounded-lg"
                  >
                    <div className="p-6">
                      <AgentTicketsPanel />
                    </div>
                  </TabsContent>

                  <TabsContent
                    value="reports"
                    className="bg-white shadow rounded-lg"
                  >
                    <div className="flex flex-col gap-6 p-6 lg:flex-row">
                      <aside className="w-full space-y-4 lg:w-72">
                        <div>
                          <h3 className="text-base font-semibold text-gray-900">
                            Performance Reports
                          </h3>
                          <p className="mt-1 text-sm text-gray-500">
                            Select a metric to review agent performance and
                            visitor insights.
                          </p>
                        </div>
                        <div className="space-y-2">
                          {REPORT_SECTIONS.map(section => {
                            const isActive = section.id === activeReportId
                            const Icon = section.icon
                            return (
                              <button
                                key={section.id}
                                type="button"
                                onClick={() => setActiveReportId(section.id)}
                                className={`w-full text-left rounded-lg border px-4 py-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                                  isActive
                                    ? 'border-blue-200 bg-blue-50 text-blue-700 shadow-sm'
                                    : 'border-gray-200 bg-white hover:border-blue-200 hover:bg-blue-50/40'
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <span
                                    className={`flex h-10 w-10 items-center justify-center rounded-md ${
                                      isActive
                                        ? 'bg-blue-100 text-blue-700'
                                        : 'bg-gray-100 text-gray-500'
                                    }`}
                                  >
                                    <Icon className="h-5 w-5" />
                                  </span>
                                  <div className="space-y-1">
                                    <p className="text-sm font-semibold">
                                      {section.label}
                                    </p>
                                    <p className="text-xs text-gray-500 leading-snug">
                                      {section.description}
                                    </p>
                                  </div>
                                </div>
                              </button>
                            )
                          })}
                        </div>
                      </aside>
                      <section className="flex-1 space-y-6">
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">
                                {selectedReportSection.label}
                              </h3>
                              <p className="mt-1 text-sm text-gray-600">
                                {selectedReportSection.description}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={resetReportFilters}
                              disabled={!hasReportFilterOverrides}
                              className="self-start text-xs font-medium text-blue-600 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              Reset filters
                            </button>
                          </div>
                          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                            {visibleReportFilters.map(filterKey => (
                              <label
                                key={filterKey}
                                className="flex flex-col gap-2 text-sm"
                              >
                                <span className="font-medium text-gray-700">
                                  {REPORT_FILTER_LABELS[filterKey]}
                                </span>
                                <select
                                  value={reportFilters[filterKey]}
                                  onChange={event =>
                                    handleReportFilterChange(
                                      filterKey,
                                      event.target.value
                                    )
                                  }
                                  className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                                >
                                  {REPORT_FILTER_OPTIONS[filterKey].map(
                                    option => (
                                      <option
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </option>
                                    )
                                  )}
                                </select>
                              </label>
                            ))}
                          </div>
                          <p className="mt-4 text-xs text-gray-500">
                            Filters update this report immediately and will also
                            apply to upcoming analytics modules.
                          </p>
                        </div>
                        {isConversationsReportSelected ? (
                          <ConversationsCountReportSection
                            report={conversationsCountQuery.data}
                            isLoading={conversationsCountQuery.isPending}
                            isFetching={conversationsCountQuery.isFetching}
                            error={conversationsCountQuery.error ?? null}
                            onRetry={() => conversationsCountQuery.refetch()}
                            activeFilterChips={activeReportFilterChips}
                          />
                        ) : (
                          <PerformanceReportSection
                            report={conversationsPerformanceQuery.data}
                            sectionId={selectedReportSection.id}
                            isLoading={conversationsPerformanceQuery.isPending}
                            isFetching={
                              conversationsPerformanceQuery.isFetching
                            }
                            error={conversationsPerformanceQuery.error ?? null}
                            onRetry={() =>
                              conversationsPerformanceQuery.refetch()
                            }
                            activeFilterChips={activeReportFilterChips}
                          />
                        )}
                      </section>
                    </div>
                  </TabsContent>

                  {(canManageTeamSetup || isSupervisor) && (
                    <TabsContent
                      value="config"
                      className="bg-white shadow rounded-lg"
                    >
                      <AgentConfigurationPanel />
                    </TabsContent>
                  )}

                  <TabsContent
                    value="chat"
                    className="bg-white shadow rounded-lg overflow-hidden min-h-120"
                  >
                    {activeChatSessionId ? (
                      <EmbeddedAgentChat sessionId={activeChatSessionId} />
                    ) : (
                      <div className="px-6 py-12 text-center text-sm text-gray-500">
                        Select a session from the left sidebar to start
                        chatting.
                      </div>
                    )}
                  </TabsContent>
                  {/* My Active Sessions */}
                  <TabsContent
                    value="my"
                    className="bg-white shadow rounded-lg"
                  >
                    <div className="px-4 py-5 sm:p-6">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                        My Active Sessions
                      </h3>

                      {activeSessions.length === 0 ? (
                        <div className="text-center py-8">
                          <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900">
                            No active sessions
                          </h3>
                          <p className="mt-1 text-sm text-gray-500">
                            Take over a session from the pending requests to
                            start helping users.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {activeSessions
                            .sort((a, b) => {
                              const aTime = new Date(
                                a.lastMessageAt || a.createdAt
                              ).getTime()
                              const bTime = new Date(
                                b.lastMessageAt || b.createdAt
                              ).getTime()
                              return bTime - aTime // Latest first
                            })
                            .map((session, index) => (
                              <div
                                key={`active-${session.id}-${index}`}
                                className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-3">
                                    <div className="flex items-center space-x-2">
                                      {getPlatformIcon(session.platform)}
                                      <button
                                        type="button"
                                        onClick={event => {
                                          event.preventDefault()
                                          event.stopPropagation()
                                          void handleCopySessionId(session.id)
                                        }}
                                        className="text-sm font-medium text-gray-900 underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-200 rounded-sm bg-transparent px-0 py-0 cursor-pointer"
                                        title="Copy session ID"
                                      >
                                        Session{' '}
                                        {session.id.slice(-8).toUpperCase()}
                                      </button>
                                      {session.isBot ? (
                                        <Bot className="h-4 w-4 text-blue-500" />
                                      ) : (
                                        <User className="h-4 w-4 text-green-500" />
                                      )}
                                      {session.agentName && (
                                        <span className="text-xs text-gray-500">
                                          {session.agentName}
                                        </span>
                                      )}
                                      {session.userOnline !== undefined && (
                                        <div
                                          className={`w-2 h-2 rounded-full ${session.userOnline ? 'bg-green-500' : 'bg-gray-300'}`}
                                        ></div>
                                      )}
                                    </div>
                                    <span className="text-xs text-gray-500">
                                      {session.messages?.length || 0} messages
                                    </span>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <span className="text-xs text-gray-500">
                                      {formatSessionTime(
                                        session.lastMessageAt ||
                                          session.createdAt
                                      )}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        openChatSession(String(session.id))
                                      }
                                      className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200"
                                    >
                                      Open Chat
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  {/* All Active Sessions */}
                  <TabsContent
                    value="all"
                    className="bg-white shadow rounded-lg"
                  >
                    <div className="px-4 py-5 sm:p-6">
                      <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                          All Requests
                        </h3>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            placeholder="Search by Session ID..."
                            value={sessionIdSearch}
                            onChange={e => setSessionIdSearch(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 w-64"
                          />
                          <select
                            value={itemsPerPage}
                            onChange={e => {
                              setItemsPerPage(Number(e.target.value))
                              setCurrentPage(1)
                            }}
                            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="10">10 per page</option>
                            <option value="25">25 per page</option>
                            <option value="50">50 per page</option>
                            <option value="100">100 per page</option>
                          </select>
                          <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <Filter className="h-4 w-4 mr-2" />
                            Filters
                            {activeFilterCount > 0 && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Active ({activeFilterCount})
                              </span>
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Filter Panel */}
                      {showFilters && (
                        <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {/* Team Filter */}
                            {/* (Removed legacy Team Filter - using All Sessions specific team filter below) */}

                            {/* Platform Filter */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Platform
                              </label>
                              <select
                                value={filters.platform}
                                onChange={e =>
                                  setFilters(prev => ({
                                    ...prev,
                                    platform: e.target.value,
                                  }))
                                }
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              >
                                <option value="all">All Platforms</option>
                                <option value="web">Web</option>
                                <option value="whatsapp">WhatsApp</option>
                                <option value="facebook">Facebook</option>
                                <option value="telegram">Telegram</option>
                                <option value="instagram">Instagram</option>
                              </select>
                            </div>

                            {/* Status Filter */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Status
                              </label>
                              <select
                                value={filters.status}
                                onChange={e =>
                                  setFilters(prev => ({
                                    ...prev,
                                    status: e.target.value,
                                  }))
                                }
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              >
                                <option value="all">Any</option>
                                <option value="pending">Pending</option>
                                <option value="accepted">Accepted</option>
                                <option value="completed">Completed</option>
                              </select>
                            </div>
                            {/* Team Filter (All Sessions specific) */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Team
                              </label>
                              <select
                                value={
                                  allSessionsTeamFilter.showAllTeams
                                    ? 'all'
                                    : String(
                                        allSessionsTeamFilter.selectedTeamId ??
                                          'all'
                                      )
                                }
                                onChange={e => {
                                  const raw = e.target.value
                                  if (raw === 'all') {
                                    handleAllSessionsTeamFilterChange(null)
                                  } else {
                                    const parsed = Number.parseInt(raw, 10)
                                    handleAllSessionsTeamFilterChange(
                                      Number.isFinite(parsed) ? parsed : null
                                    )
                                  }
                                }}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              >
                                <option value="all">All Teams</option>
                                {agentTeams.map(team => (
                                  <option key={team.id} value={team.id}>
                                    {team.name}
                                  </option>
                                ))}
                              </select>
                            </div>

                            {/* Date Range Filter */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Created
                              </label>
                              <select
                                value={filters.dateRange}
                                onChange={e =>
                                  setFilters(prev => ({
                                    ...prev,
                                    dateRange: e.target.value,
                                  }))
                                }
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              >
                                <option value="all">All Time</option>
                                <option value="last-hour">Last Hour</option>
                                <option value="last-4-hours">
                                  Last 4 Hours
                                </option>
                                <option value="today">Today</option>
                              </select>
                            </div>

                            {/* Message Count Filter - Hidden for Request tab, always defaults to 'all' */}
                            <div style={{ display: 'none' }}>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Activity
                              </label>
                              <select
                                value={filters.messageCount}
                                onChange={e =>
                                  setFilters(prev => ({
                                    ...prev,
                                    messageCount: e.target.value,
                                  }))
                                }
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              >
                                <option value="all">All Activity</option>
                                <option value="low">Low (1-5 messages)</option>
                                <option value="medium">
                                  Medium (6-20 messages)
                                </option>
                                <option value="high">
                                  High (20+ messages)
                                </option>
                              </select>
                            </div>

                            {/* Priority Filter */}
                            {/* <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Priority
                            </label>
                            <select
                              value={filters.priority}
                              onChange={e =>
                                setFilters(prev => ({
                                  ...prev,
                                  priority: e.target.value,
                                }))
                              }
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            >
                              <option value="all">All Priorities</option>
                              <option value="urgent">Urgent</option>
                              <option value="high">High</option>
                              <option value="normal">Normal</option>
                              <option value="low">Low</option>
                            </select>
                          </div> */}
                          </div>

                          {/* Filter Actions */}
                          <div className="mt-4 flex items-center justify-between">
                            <div className="text-sm text-gray-500">
                              Showing {visibleRangeStart}-{visibleRangeEnd} of{' '}
                              {matchingSessionsCount} sessions
                              {matchingSessionsCount !==
                                filteredSessions.length &&
                                ` (search narrowed from ${filteredSessions.length})`}
                              {filteredSessions.length !==
                                allActiveSessions.length &&
                                ` (filtered from ${allActiveSessions.length})`}
                            </div>
                            <button
                              onClick={handleResetAllSessionsFilters}
                              className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              <X className="h-3 w-3 mr-1" />
                              Clear Filters
                            </button>
                          </div>
                        </div>
                      )}

                      {matchingSessionsCount === 0 ? (
                        <div className="text-center py-8">
                          <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900">
                            {allActiveSessions.length === 0
                              ? 'No sessions available'
                              : 'No sessions match your filters'}
                          </h3>
                          <p className="mt-1 text-sm text-gray-500">
                            {allActiveSessions.length === 0
                              ? 'There are no sessions to display yet.'
                              : 'Try adjusting your filter or search terms to see more sessions.'}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {currentSessions
                            .sort((a, b) => {
                              const priorityDifference =
                                getSessionPriority(a) - getSessionPriority(b)

                              if (priorityDifference !== 0) {
                                return priorityDifference
                              }

                              return (
                                getSessionActivityTimestamp(b) -
                                getSessionActivityTimestamp(a)
                              )
                            })
                            .map((session, index) => {
                              // Unified team-centric status (preferred) with graceful fallback
                              const unified = (session as any)
                                .handoverStatus as
                                | {
                                    label?: string
                                    color?: string
                                    key?: string
                                    description?: string
                                  }
                                | undefined

                              const statusText =
                                unified?.label ||
                                session.handoverIndication?.flag ||
                                (session.pendingHandoverRequest
                                  ? 'Handover Pending'
                                  : session.status
                                    ? session.status
                                        .split(/[\s_-]+/)
                                        .map(
                                          p =>
                                            p.charAt(0).toUpperCase() +
                                            p.slice(1)
                                        )
                                        .join(' ')
                                    : 'Unknown')

                              const colorToken =
                                unified?.color ||
                                session.handoverIndication?.color ||
                                'green'

                              const getBadgeClasses = () => {
                                const colorMap: Record<string, string> = {
                                  gray: 'bg-gray-100 text-gray-700',
                                  purple: 'bg-purple-100 text-purple-700',
                                  blue: 'bg-blue-100 text-blue-700',
                                  yellow: 'bg-yellow-100 text-yellow-700',
                                  green: 'bg-green-100 text-green-700',
                                }
                                return (
                                  colorMap[colorToken] ||
                                  'bg-green-100 text-green-700'
                                )
                              }

                              const messagePreviewRaw =
                                session.lastMessage?.content?.trim() ?? ''
                              const messagePreview =
                                messagePreviewRaw.length > 0
                                  ? messagePreviewRaw.length > 200
                                    ? `${messagePreviewRaw.slice(0, 197)}…`
                                    : messagePreviewRaw
                                  : 'No recent messages.'

                              const contactSegments = new Set<string>()
                              if (session.contact?.name) {
                                contactSegments.add(session.contact.name)
                              }
                              if (session.contact?.email) {
                                contactSegments.add(session.contact.email)
                              }
                              if (session.contact?.phoneNumber) {
                                contactSegments.add(session.contact.phoneNumber)
                              }
                              if (session.contact?.identifier) {
                                contactSegments.add(session.contact.identifier)
                              }
                              if (
                                contactSegments.size === 0 &&
                                session.platformId
                              ) {
                                contactSegments.add(session.platformId)
                              }

                              const contactDisplay =
                                contactSegments.size > 0
                                  ? Array.from(contactSegments).join(' • ')
                                  : null

                              // Determine effective team name for badge:
                              // ONLY use team_id from handover_requests, NOT from agent's team membership

                              // Use teamId and teamName from backend (already calculated correctly)
                              const effectiveTeamName = session.teamName || null
                              const teamIdToUse = session.teamId || null

                              return (
                                <div
                                  key={`all-${session.id}-${index}`}
                                  className="rounded-lg border border-gray-200 p-4 transition-colors hover:bg-gray-50"
                                >
                                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                    <div className="space-y-2">
                                      <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-gray-900">
                                        {getPlatformIcon(session.platform)}
                                        <button
                                          type="button"
                                          onClick={event => {
                                            event.preventDefault()
                                            event.stopPropagation()
                                            void handleCopySessionId(session.id)
                                          }}
                                          className="underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-200"
                                          title="Copy session ID"
                                        >
                                          Session{' '}
                                          {session.id.slice(-8).toUpperCase()}
                                        </button>
                                        {session.isBot ? (
                                          <Bot className="h-4 w-4 text-blue-500" />
                                        ) : session.isHandedOver ? (
                                          <User className="h-4 w-4 text-green-500" />
                                        ) : null}
                                        <span
                                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${getBadgeClasses()}`}
                                        >
                                          {statusText}
                                        </span>
                                        {session.userOnline !== undefined && (
                                          <span
                                            className={`inline-flex h-2 w-2 rounded-full ${session.userOnline ? 'bg-green-500' : 'bg-gray-300'}`}
                                          />
                                        )}
                                      </div>

                                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                                        <span className="inline-flex items-center gap-1">
                                          <Clock className="h-3.5 w-3.5 text-gray-400" />
                                          {formatSessionTime(
                                            session.lastMessageAt ||
                                              session.createdAt
                                          )}
                                        </span>
                                        {session.pendingHandoverRequest && (
                                          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200">
                                            <Flag className="h-3.5 w-3.5" />
                                            {formatPendingPriorityLabel(
                                              session.pendingHandoverRequest
                                                .priority
                                            )}
                                          </span>
                                        )}
                                        <span className="inline-flex items-center gap-1">
                                          <User className="h-3.5 w-3.5 text-gray-400" />
                                          {session.agentName ||
                                            (session.handoverIndication
                                              ?.flag === 'Completed' &&
                                            session.lastAssignment?.agentName
                                              ? session.lastAssignment.agentName
                                              : session.handoverIndication
                                                    ?.flag === 'Transferred' &&
                                                  session.lastAssignment
                                                    ?.assignedTo
                                                ? `Agent ${session.lastAssignment.assignedTo}`
                                                : 'Unassigned')}
                                        </span>
                                        {effectiveTeamName && (
                                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                                            <Users className="h-3.5 w-3.5 text-slate-500" />
                                            {effectiveTeamName}
                                          </span>
                                        )}
                                        <span className="inline-flex items-center gap-1">
                                          <Globe className="h-3.5 w-3.5 text-gray-400" />
                                          {formatPlatformLabel(
                                            session.platform
                                          )}
                                        </span>
                                      </div>

                                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                                        {contactDisplay ? (
                                          <span className="inline-flex items-center gap-1">
                                            <Phone className="h-3.5 w-3.5 text-gray-400" />
                                            {contactDisplay}
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center gap-1 text-gray-400">
                                            <Phone className="h-3.5 w-3.5 text-gray-300" />
                                            No contact info
                                          </span>
                                        )}
                                        {session.pendingHandoverRequest
                                          ?.requestedAt && (
                                          <span className="inline-flex items-center gap-1 text-amber-600">
                                            <Clock className="h-3.5 w-3.5 text-amber-500" />
                                            Requested{' '}
                                            {formatSessionTime(
                                              session.pendingHandoverRequest
                                                .requestedAt
                                            )}
                                          </span>
                                        )}
                                        {session.lastAssignment?.assignedAt && (
                                          <span className="inline-flex items-center gap-1">
                                            <Clock className="h-3.5 w-3.5 text-gray-400" />
                                            Assigned{' '}
                                            {formatSessionTime(
                                              session.lastAssignment.assignedAt
                                            )}
                                          </span>
                                        )}
                                      </div>

                                      <div className="text-sm text-gray-600">
                                        {messagePreview}
                                      </div>
                                    </div>

                                    <div className="flex flex-wrap items-start justify-end gap-2">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          openChatSession(String(session.id))
                                        }
                                        className="inline-flex items-center rounded bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-200"
                                      >
                                        Open Chat
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                        </div>
                      )}

                      {/* Pagination Controls */}
                      {matchingSessionsCount > itemsPerPage && (
                        <div className="mt-6 px-4 py-3 border-t border-gray-200">
                          <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-700">
                              Page {currentPage} of {totalPages}
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() =>
                                  setCurrentPage(Math.max(1, currentPage - 1))
                                }
                                disabled={currentPage === 1}
                                className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <ChevronLeft className="h-4 w-4" />
                              </button>

                              {/* Page numbers */}
                              {Array.from(
                                { length: Math.min(5, totalPages) },
                                (_, i) => {
                                  const pageNum =
                                    Math.max(
                                      1,
                                      Math.min(totalPages - 4, currentPage - 2)
                                    ) + i
                                  if (pageNum > totalPages) return null

                                  return (
                                    <button
                                      key={pageNum}
                                      onClick={() => setCurrentPage(pageNum)}
                                      className={`px-3 py-2 rounded-md text-sm font-medium ${
                                        pageNum === currentPage
                                          ? 'bg-blue-600 text-white'
                                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                      }`}
                                    >
                                      {pageNum}
                                    </button>
                                  )
                                }
                              )}

                              <button
                                onClick={() =>
                                  setCurrentPage(
                                    Math.min(totalPages, currentPage + 1)
                                  )
                                }
                                disabled={currentPage === totalPages}
                                className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <ChevronRight className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </ErrorBoundary>
            </div>

            <div className="mt-8">
              <OutboundMessaging isSupervisor={isSupervisor} />
            </div>

            {/* Team Selection Modal for Takeover */}
            <Dialog
              open={showTakeoverTeamModal}
              onOpenChange={open => {
                if (!open) {
                  closeTakeoverTeamModal()
                }
              }}
            >
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {pendingAcceptRequestId
                      ? 'Select Team for Accept'
                      : 'Select Team for Takeover'}
                  </DialogTitle>
                  <DialogDescription>
                    This session is a broadcast handover. Please select which
                    team you want to{' '}
                    {pendingAcceptRequestId ? 'accept' : 'take over'} it as.
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <Label htmlFor="takeover-team-select">Team</Label>
                  <Select
                    value={selectedTakeoverTeamId?.toString() || ''}
                    onValueChange={value => {
                      if (!value) {
                        setSelectedTakeoverTeamId(null)
                        return
                      }
                      setSelectedTakeoverTeamId(Number(value))
                    }}
                  >
                    <SelectTrigger id="takeover-team-select" className="mt-2">
                      <SelectValue placeholder="Select a team" />
                    </SelectTrigger>
                    <SelectContent>
                      {agentTeams.map(team => (
                        <SelectItem key={team.id} value={team.id.toString()}>
                          {team.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => {
                      closeTakeoverTeamModal()
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirmTakeoverTeamSelection}
                    disabled={
                      selectedTakeoverTeamId == null || isTakeoverTeamSubmitting
                    }
                  >
                    {isTakeoverTeamSubmitting ? (
                      <span className="inline-flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {pendingAcceptRequestId
                          ? 'Confirming Accept…'
                          : 'Confirming Takeover…'}
                      </span>
                    ) : pendingAcceptRequestId ? (
                      'Confirm Accept'
                    ) : (
                      'Confirm Takeover'
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <SupervisorManageDialog
              open={isSupervisorModalOpen}
              onOpenChange={open => {
                setIsSupervisorModalOpen(open)
                if (!open) {
                  setSupervisorSessionId(null)
                  setSupervisorActionError(null)
                }
              }}
              sessionSummary={{
                id: supervisorSessionId,
                platform: supervisorSessionDetails?.platform ?? null,
                assignedAgentName: supervisorSessionDetails?.agentName ?? null,
                assignedAgentId:
                  supervisorSessionDetails?.agentId != null
                    ? Number.parseInt(
                        String(supervisorSessionDetails.agentId),
                        10
                      ) || null
                    : null,
                assignedTeamId:
                  supervisorSessionDetails?.teamId != null
                    ? Number.parseInt(
                        String(supervisorSessionDetails.teamId),
                        10
                      ) || null
                    : null,
                assignedTeamName: supervisorSessionDetails?.teamName ?? null,
                pendingHandoverRequest:
                  supervisorSessionDetails?.pendingHandoverRequest ?? null,
                lastAssignment:
                  supervisorSessionDetails?.lastAssignment ?? null,
              }}
              myTeamIds={userTeamIdList}
              agents={availableAgentsForSupervisor}
              isLoadingAgents={isFetchingSupervisorAgents}
              agentsError={supervisorAgentsErrorMessage}
              onSubmit={handleSupervisorSubmit}
              isSubmitting={isSupervisorActionLoading}
              actionError={supervisorActionError}
              defaultTeamId={supervisorDefaultTeamId}
              actionLabel="Reassign"
            />
          </div>
        </div>
      </main>
    </div>
  )
}
